import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import {
  Search, Filter, ShoppingCart, ChevronDown, ChevronRight,
  ArrowLeft, Download, Database, Layers, FileText,
  Check, Trash2, X, Activity, Info, Sparkles, Loader2,
  ClipboardList, User, Briefcase, PieChart, ArrowRightLeft,
  Share2, ZoomIn, ZoomOut, Move, Maximize2, Minimize2,
  ArrowUp, ArrowDown, RotateCcw, Shield, Lock, ExternalLink,
  BarChart3, Package, Brain, Dna, LayoutGrid, List, Table, Menu, Clock, ArrowRight
} from 'lucide-react';

// --- MOCK DATA GENERATOR ---


// --- UTILS ---
const classNames = (...classes) => classes.filter(Boolean).join(' ');

const fetchWithBackoff = async (url, options, retries = 5) => {
  const delays = [1000, 2000, 4000, 8000, 16000];
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, options);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return await res.json();
    } catch (err) {
      if (i === retries - 1) throw err;
      await new Promise(r => setTimeout(r, delays[i]));
    }
  }
};

const parseCompleteness = (val) => {
  if (val === null || val === undefined || val === '') {
    return { percent: 0, color: 'bg-slate-300', text: 'N/A' };
  }
  const strVal = String(val).trim();
  let num = parseFloat(strVal);
  if (isNaN(num)) return { percent: 0, color: 'bg-slate-300', text: 'N/A' };
  if (!strVal.endsWith('%') && num <= 1 && num >= 0) num = num * 100;
  num = Math.max(0, Math.min(100, num));
  let color = 'bg-emerald-500';
  if (num < 50) color = 'bg-red-500';
  else if (num < 80) color = 'bg-amber-400';
  const text = `${num.toFixed(1).replace(/\.0$/, '')}%`;
  return { percent: num, color, text };
};

const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * (typeof end === 'number' ? end : parseFloat(end))));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [hasStarted, end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};

const ScrollReveal = ({ children, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(domRef.current);
        }
      });
    }, { threshold: 0.1 });

    const current = domRef.current;
    if (current) {
      observer.observe(current);
    }
    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={classNames(
        className,
        "transition-all duration-1000 ease-out transform",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      )}
    >
      {children}
    </div>
  );
};

const RecommendationPopup = ({ visible, onClose, sourceVar, recommendations, loading, onAdd, cart }) => {
  const [isMinimized, setIsMinimized] = useState(true);

  if (!visible) return null;
  return (
    <div className={classNames(
      "fixed bottom-6 right-6 z-[60] bg-white rounded-2xl shadow-2xl border border-purple-100 overflow-hidden transition-all duration-300 ease-in-out flex flex-col animate-in slide-in-from-bottom-5 fade-in",
      isMinimized ? "w-64 h-14" : "w-80 h-[480px]"
    )}>
      <div
        className="bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue p-4 flex items-center justify-between text-white cursor-pointer shrink-0"
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-bold truncate">
            {isMinimized ? `Suggestions (${recommendations.length})` : `Discovery Suggestions (${recommendations.length})`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
            className="hover:bg-white/20 rounded-lg p-1 transition-colors"
            title={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="hover:bg-white/20 rounded-lg p-1 transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="p-4 bg-purple-50/50 border-b border-purple-100 shrink-0">
            <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1">Added to Cart</p>
            <p className="text-xs font-bold text-slate-800 line-clamp-1">{sourceVar?.['Variable Label'] || sourceVar?.['Variable Name']}</p>
          </div>

          <div className="p-2 flex-1 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Finding matches...</p>
              </div>
            ) : recommendations.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-xs italic">No additional matches found.</div>
            ) : (
              <div className="flex flex-col gap-1">
                {recommendations.map(variable => {
                  const isInCart = cart.includes(variable.id);
                  return (
                    <div key={variable.id} className="p-3 hover:bg-slate-50 rounded-xl transition-colors group flex items-start gap-3 border border-transparent hover:border-slate-100 text-left">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-[9px] font-black text-slate-800 bg-slate-100 px-1 py-0.5 rounded uppercase">{variable['Variable Name']}</span>
                          <p className="text-[9px] font-bold text-slate-400 uppercase truncate">{variable.Cohort}</p>
                        </div>
                        <p className="text-xs font-bold text-slate-800 line-clamp-2 leading-tight mb-1.5">{variable['Variable Label']}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 opacity-60" style={{ width: `${(variable.similarityScore || 0) * 100}%` }}></div>
                          </div>
                          <span className="text-[9px] font-black text-purple-600 italic">{Math.round((variable.similarityScore || 0) * 100)}% Match</span>
                        </div>
                      </div>
                      <button
                        onClick={() => onAdd(variable.id)}
                        disabled={isInCart}
                        className={classNames(
                          "p-2 rounded-lg transition-all shadow-sm shrink-0",
                          isInCart ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-purple-50 text-purple-600 hover:bg-purple-600 hover:text-white"
                        )}
                      >
                        {isInCart ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {!loading && recommendations.length > 0 && (
            <div className="p-3 bg-slate-50 border-t border-slate-100">
              <button
                onClick={() => {
                  const newIds = recommendations.map(r => r.id).filter(id => !cart.includes(id));
                  if (newIds.length > 0) {
                    newIds.forEach(id => onAdd(id));
                  }
                  onClose();
                }}
                className="w-full py-2 bg-white border border-purple-200 text-purple-700 text-[10px] font-black uppercase tracking-widest rounded-lg hover:bg-purple-600 hover:text-white hover:border-purple-600 transition-all shadow-sm"
              >
                Add All Suggestions
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// --- MAIN APP COMPONENT ---
export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const knownViews = ['home', 'data-discovery', 'cohort-library', 'matrix', 'directory', 'global-search', 'cohort', 'cart', 'overlap', 'synthetic', 'network', 'data-access', 'support'];
  const currentPathTarget = location.pathname.slice(1);
  const currentView = knownViews.includes(currentPathTarget) ? currentPathTarget : (currentPathTarget === '' ? 'home' : 'home');

  const setCurrentView = (view) => {
    navigate(`/${view === 'home' ? '' : view}`);
    // Optional: force scroll to top on navigation
    window.scrollTo(0, 0);
  };

  const [selectedCohort, setSelectedCohort] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [groupBy, setGroupBy] = useState('Category');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedDirCohorts, setExpandedDirCohorts] = useState({});
  const [activeDirCategory, setActiveDirCategory] = useState(null);
  const [dirCategoryPages, setDirCategoryPages] = useState({});
  const [expandedCartCohorts, setExpandedCartCohorts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [matrixSort, setMatrixSort] = useState({ column: null, direction: 'desc' });
  const [lastTargetedCategory, setLastTargetedCategory] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [isAiSearching, setIsAiSearching] = useState(false);
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [aiSearchError, setAiSearchError] = useState(null);
  const [userName, setUserName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');

  // Global Semantic Search State
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [isGlobalSearching, setIsGlobalSearching] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState(null);
  const [globalSearchError, setGlobalSearchError] = useState(null);
  const [semanticModelLoading, setSemanticModelLoading] = useState(false);
  const [semanticAssets, setSemanticAssets] = useState(null);

  // Similar Variables Modal State
  const [similarModalOpen, setSimilarModalOpen] = useState(false);
  const [similarOriginVar, setSimilarOriginVar] = useState(null);
  const [similarVars, setSimilarVars] = useState([]);
  const [isSimilarLoading, setIsSimilarLoading] = useState(false);

  // Cohort Overlap State
  const [overlapCohortA, setOverlapCohortA] = useState('');
  const [overlapCohortB, setOverlapCohortB] = useState('');
  const [overlapThreshold, setOverlapThreshold] = useState(0.75);
  const [isOverlapCalculating, setIsOverlapCalculating] = useState(false);
  const [overlapResults, setOverlapResults] = useState(null);
  const [overlapError, setOverlapError] = useState(null);

  // Network State
  const [networkData, setNetworkData] = useState({ nodes: [], links: [] });
  const [isNetworkCalculating, setIsNetworkCalculating] = useState(false);
  const [networkError, setNetworkError] = useState(null);
  const [networkScale, setNetworkScale] = useState(0.75);
  const [networkTranslate, setNetworkTranslate] = useState({ x: 0, y: 0 });
  const [selectedNetworkNode, setSelectedNetworkNode] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPos, setLastPanPos] = useState({ x: 0, y: 0 });

  // Matrix View State
  const [matrixViewType, setMatrixViewType] = useState('phenotypic'); // 'phenotypic' | 'imaging'
  const [imagingMatrixData, setImagingMatrixData] = useState({ headers: [], data: {} });
  const [imagingCart, setImagingCart] = useState([]); // [{ cohort, type }]
  const [isImagingCartExpanded, setIsImagingCartExpanded] = useState(false);

  const [genomicsMatrixData, setGenomicsMatrixData] = useState({ headers: [], data: {} });
  const [genomicsCart, setGenomicsCart] = useState([]); // [{ cohort, type }]
  const [isGenomicsCartExpanded, setIsGenomicsCartExpanded] = useState(false);

  const [cohortDescriptives, setCohortDescriptives] = useState([]);

  // Synthetic Data State
  const [syntheticAccessModalOpen, setSyntheticAccessModalOpen] = useState(false);
  const [requestingDataset, setRequestingDataset] = useState(null);
  const [authorizedDatasets, setAuthorizedDatasets] = useState([]);
  const [syntheticForm, setSyntheticForm] = useState({
    fullName: '',
    email: '',
    institution: '',
    jobRole: '',
    plannedUse: ''
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSentStatus, setEmailSentStatus] = useState(null); // 'success' | 'error'

  // Research Question Search State
  const [researchQuestion, setResearchQuestion] = useState('');
  const [isResearchSearching, setIsResearchSearching] = useState(false);
  const [researchCohortResults, setResearchCohortResults] = useState(null);
  const [researchSearchError, setResearchSearchError] = useState(null);

  // Recommendation Popup State
  const [recPopup, setRecPopup] = useState({ visible: false, sourceVar: null, recommendations: [] });
  const [isRecPopupLoading, setIsRecPopupLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/metadata/categorised_variables_combined.csv`);
        const text = await response.text();

        // Robust CSV parsing to handle multiline quoted strings
        const rows = [];
        let currentRow = [];
        let currentField = '';
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const nextChar = text[i + 1];

          if (inQuotes) {
            if (char === '"') {
              if (nextChar === '"') {
                currentField += '"';
                i++;
              } else {
                inQuotes = false;
              }
            } else {
              currentField += char;
            }
          } else {
            if (char === '"') {
              inQuotes = true;
            } else if (char === ',') {
              currentRow.push(currentField.trim());
              currentField = '';
            } else if (char === '\n' || char === '\r') {
              if (currentField || currentRow.length > 0) {
                currentRow.push(currentField.trim());
                rows.push(currentRow);
                currentField = '';
                currentRow = [];
              }
              if (char === '\r' && nextChar === '\n') i++;
            } else {
              currentField += char;
            }
          }
        }

        if (currentField || currentRow.length > 0) {
          currentRow.push(currentField.trim());
          rows.push(currentRow);
        }

        if (rows.length < 2) {
          setLoading(false);
          return;
        }

        const headers = rows[0];
        const parsedData = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (row.length < headers.length) continue;

          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || '';
          });

          if (obj.Cohort && obj['Variable Name']) {
            obj.id = `${obj.Cohort}___${obj['Variable Name']}___row_${i}`;
            parsedData.push(obj);
          }
        }

        setData(parsedData);

        // Pre-load network data
        try {
          const netRes = await fetch(`${process.env.PUBLIC_URL}/metadata/network.json`);
          if (netRes.ok) {
            const netJson = await netRes.json();
            setNetworkData(netJson);
          }
        } catch (e) {
          console.warn('Precalculated network not found, will calculate on demand.');
        }

        // Load Imaging Matrix
        try {
          const imgRes = await fetch(`${process.env.PUBLIC_URL}/metadata/Imaging_Matrix.csv`);
          const imgText = await imgRes.text();
          const imgRows = [];
          let curImgRow = [];
          let curImgField = '';
          let inImgQuotes = false;
          for (let i = 0; i < imgText.length; i++) {
            const char = imgText[i];
            const nextChar = imgText[i + 1];
            if (inImgQuotes) {
              if (char === '"' && nextChar === '"') { curImgField += '"'; i++; }
              else if (char === '"') inImgQuotes = false;
              else curImgField += char;
            } else {
              if (char === '"') inImgQuotes = true;
              else if (char === ',') { curImgRow.push(curImgField.trim()); curImgField = ''; }
              else if (char === '\n' || char === '\r') {
                if (curImgField || curImgRow.length > 0) { curImgRow.push(curImgField.trim()); imgRows.push(curImgRow); curImgField = ''; curImgRow = []; }
                if (char === '\r' && nextChar === '\n') i++;
              } else curImgField += char;
            }
          }
          if (curImgField || curImgRow.length > 0) { curImgRow.push(curImgField.trim()); imgRows.push(curImgRow); }

          if (imgRows.length > 1) {
            const imgHeaders = imgRows[0].slice(1); // Skip 'Cohort'
            const imgDataItems = {};
            for (let i = 1; i < imgRows.length; i++) {
              const r = imgRows[i];
              if (!r[0]) continue;
              const cohortName = r[0];
              const statuses = {};
              imgHeaders.forEach((h, idx) => {
                statuses[h] = r[idx + 1] === 'Collected';
              });
              imgDataItems[cohortName] = statuses;
            }
            setImagingMatrixData({ headers: imgHeaders, data: imgDataItems });
          }
        } catch (e) {
          console.error('Error loading imaging matrix:', e);
        }

        // Load Genomics Matrix
        try {
          const genRes = await fetch(`${process.env.PUBLIC_URL}/metadata/Genomics_Metadata.csv`);
          const genText = await genRes.text();
          const genRows = [];
          let curGenRow = [];
          let curGenField = '';
          let inGenQuotes = false;
          for (let i = 0; i < genText.length; i++) {
            const char = genText[i];
            const nextChar = genText[i + 1];
            if (inGenQuotes) {
              if (char === '"' && nextChar === '"') { curGenField += '"'; i++; }
              else if (char === '"') inGenQuotes = false;
              else curGenField += char;
            } else {
              if (char === '"') inGenQuotes = true;
              else if (char === ',') { curGenRow.push(curGenField.trim()); curGenField = ''; }
              else if (char === '\n' || char === '\r') {
                if (curGenField || curGenRow.length > 0) { curGenRow.push(curGenField.trim()); genRows.push(curGenRow); curGenField = ''; curGenRow = []; }
                if (char === '\r' && nextChar === '\n') i++;
              } else curGenField += char;
            }
          }
          if (curGenField || curGenRow.length > 0) { curGenRow.push(curGenField.trim()); genRows.push(curGenRow); }

          if (genRows.length > 1) {
            const genHeaders = genRows[0].slice(1);
            const genDataItems = {};
            for (let i = 1; i < genRows.length; i++) {
              const r = genRows[i];
              if (!r[0]) continue;
              const cohortName = r[0];
              const statuses = {};
              genHeaders.forEach((h, idx) => {
                statuses[h] = r[idx + 1] === 'Collected';
              });
              genDataItems[cohortName] = statuses;
            }
            setGenomicsMatrixData({ headers: genHeaders, data: genDataItems });
          }
        } catch (e) {
          console.error('Error loading genomics matrix:', e);
        }

        // Load Cohort Descriptives
        try {
          const cohortRes = await fetch(`${process.env.PUBLIC_URL}/metadata/Cohort Descriptives.csv`);
          const cohortText = await cohortRes.text();
          const cohortRows = [];
          let curRow = [];
          let curField = '';
          let inQuotes = false;
          for (let i = 0; i < cohortText.length; i++) {
            const char = cohortText[i];
            const nextChar = cohortText[i + 1];
            if (inQuotes) {
              if (char === '"' && nextChar === '"') { curField += '"'; i++; }
              else if (char === '"') inQuotes = false;
              else curField += char;
            } else {
              if (char === '"') inQuotes = true;
              else if (char === ',') { curRow.push(curField.trim()); curField = ''; }
              else if (char === '\n' || char === '\r') {
                if (curField || curRow.length > 0) { curRow.push(curField.trim()); cohortRows.push(curRow); curField = ''; curRow = []; }
                if (char === '\r' && nextChar === '\n') i++;
              } else curField += char;
            }
          }
          if (curField || curRow.length > 0) { curRow.push(curField.trim()); cohortRows.push(curRow); }

          if (cohortRows.length > 1) {
            const headers = cohortRows[0];
            const parsedCohorts = [];
            for (let i = 1; i < cohortRows.length; i++) {
              const r = cohortRows[i];
              if (r.length < headers.length) continue;
              const obj = {};
              headers.forEach((h, idx) => {
                obj[h] = r[idx] || '';
              });
              parsedCohorts.push(obj);
            }
            setCohortDescriptives(parsedCohorts);
          }
        } catch (e) {
          console.error('Error loading cohort descriptives:', e);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Effect to handle scroll-to-category when navigating from Matrix
  useEffect(() => {
    if (currentView === 'cohort' && lastTargetedCategory) {
      // Delay to ensure the DOM is fully rendered
      const timer = setTimeout(() => {
        const id = `cat-${lastTargetedCategory.replace(/\s+/g, '-')}`;
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Optional: briefly highlight the category
          element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-4');
          setTimeout(() => element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-4'), 2000);
        }
        setLastTargetedCategory(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentView, lastTargetedCategory]);

  // Network Simulation Effect
  useEffect(() => {
    if (currentView !== 'network' || networkData.nodes.length === 0) return;

    let animationFrame;
    const simulate = () => {
      setNetworkData(prev => {
        const nodes = prev.nodes.map(n => ({ ...n }));
        const links = prev.links;
        const friction = 0.85;
        const center = { x: 500, y: 400 };

        // 1. Repulsion (Spread nodes out)
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dx = nodes[j].x - nodes[i].x;
            const dy = nodes[j].y - nodes[i].y;
            const distSq = dx * dx + dy * dy || 1;
            const dist = Math.sqrt(distSq);

            // Limit force density to avoid "explosions"
            const repulsionStrength = 55000;
            const force = Math.min(100, repulsionStrength / Math.max(distSq, 500));

            const fx = (dx / dist) * force;
            const fy = (dy / dist) * force;
            nodes[i].vx -= fx;
            nodes[i].vy -= fy;
            nodes[j].vx += fx;
            nodes[j].vy += fy;
          }
        }

        // 2. Attraction (links)
        const nodeMap = {};
        nodes.forEach((n, idx) => { nodeMap[n.id] = idx; });

        links.forEach(link => {
          const sIdx = nodeMap[link.source];
          const tIdx = nodeMap[link.target];
          if (sIdx === undefined || tIdx === undefined) return;
          const s = nodes[sIdx];
          const t = nodes[tIdx];
          const dx = t.x - s.x;
          const dy = t.y - s.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;

          // Normalized strength to prevent rapid elastic movement
          const strength = Math.min(0.05, link.value * 0.001);
          const idealDist = 650;
          const force = (dist - idealDist) * strength;

          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          nodes[sIdx].vx += fx;
          nodes[sIdx].vy += fy;
          nodes[tIdx].vx -= fx;
          nodes[tIdx].vy -= fy;
        });

        // 3. Center Gravity & Frictional Update
        const springBack = 0.001;
        const frictionCoeff = 0.75; // Increased friction (from 0.85) to reach equilibrium faster

        nodes.forEach((n, i) => {
          n.vx += (center.x - n.x) * springBack;
          n.vy += (center.y - n.y) * springBack;

          // Cap velocity to prevent screen-flying
          n.vx = Math.max(-20, Math.min(20, n.vx));
          n.vy = Math.max(-20, Math.min(20, n.vy));

          n.x += n.vx;
          n.y += n.vy;

          n.vx *= frictionCoeff;
          n.vy *= frictionCoeff;
        });

        // Update positions array for fast lookup
        let totalMovement = 0;
        const nodePos = {};
        nodes.forEach(n => {
          nodePos[n.id] = { x: n.x, y: n.y };
          totalMovement += Math.abs(n.vx) + Math.abs(n.vy);
        });

        // Kill animation if settled to save CPU and stop the "wiggle"
        if (totalMovement < 0.1) {
          cancelAnimationFrame(animationFrame);
          return { ...prev, nodes, nodePos };
        }

        return { ...prev, nodes, nodePos };
      });
      animationFrame = requestAnimationFrame(simulate);
    };

    animationFrame = requestAnimationFrame(simulate);
    return () => cancelAnimationFrame(animationFrame);
  }, [currentView, networkData.nodes.length === 0]);

  useEffect(() => {
    if (currentView === 'network' && networkData.nodes.length === 0 && !isNetworkCalculating) {
      handleCalculateNetwork();
    }
  }, [currentView]);

  useEffect(() => {
    if (currentView === 'global-search' && globalSearchQuery && globalSearchResults === null && !isGlobalSearching) {
      handleGlobalSemanticSearch();
    }
  }, [currentView, globalSearchResults, isGlobalSearching]);

  const allCategories = useMemo(() => {
    const catsMap = {};
    data.forEach(d => {
      const cat = d.Category;
      if (cat && String(cat).trim() !== '') {
        if (!catsMap[cat]) catsMap[cat] = new Set();
        catsMap[cat].add(d.Cohort);
      }
    });

    return Object.keys(catsMap).sort((a, b) => {
      const countDiff = catsMap[b].size - catsMap[a].size;
      return countDiff !== 0 ? countDiff : a.localeCompare(b);
    });
  }, [data]);

  const allCohorts = useMemo(() => {
    const cohorts = new Set(data.map(d => d.Cohort));
    return Array.from(cohorts).sort();
  }, [data]);

  // DATA DERIVATION FOR MATRIX
  const matrixData = useMemo(() => {
    const matrix = {};
    let maxVal = 0;
    allCohorts.forEach(cohort => {
      matrix[cohort] = {};
      allCategories.forEach(cat => { matrix[cohort][cat] = 0; });
    });
    data.forEach(row => {
      if (matrix[row.Cohort] && matrix[row.Cohort][row.Category] !== undefined) {
        matrix[row.Cohort][row.Category]++;
        if (matrix[row.Cohort][row.Category] > maxVal) maxVal = matrix[row.Cohort][row.Category];
      }
    });
    return { matrix, maxVal };
  }, [data, allCohorts, allCategories]);

  const visibleCohorts = useMemo(() => {
    let cohorts = [...allCohorts];
    const { matrix } = matrixData;

    // Filter by required categories
    if (selectedCategories.length > 0) {
      cohorts = cohorts.filter(cohort => selectedCategories.every(cat => matrix[cohort][cat] > 0));
    }

    // Sort by selected column
    if (matrixSort.column) {
      cohorts.sort((a, b) => {
        const valA = matrix[a][matrixSort.column] || 0;
        const valB = matrix[b][matrixSort.column] || 0;
        return matrixSort.direction === 'desc' ? valB - valA : valA - valB;
      });
    }

    return cohorts;
  }, [allCohorts, selectedCategories, matrixData, matrixSort]);

  const allImagingCohorts = useMemo(() => {
    return Object.keys(imagingMatrixData.data).sort();
  }, [imagingMatrixData.data]);

  const allGenomicsCohorts = useMemo(() => {
    return Object.keys(genomicsMatrixData.data).sort();
  }, [genomicsMatrixData.data]);

  // DATA DERIVATION FOR CART (Moved to top level to avoid Hook violations)
  const cartItems = useMemo(() => data.filter(d => cart.includes(d.id)), [data, cart]);

  const cartByCohort = useMemo(() => {
    return cartItems.reduce((acc, curr) => {
      if (!acc[curr.Cohort]) acc[curr.Cohort] = [];
      acc[curr.Cohort].push(curr);
      return acc;
    }, {});
  }, [cartItems]);

  const cartCohorts = useMemo(() => {
    const phenotpyicCohorts = Object.keys(cartByCohort);
    const imagingCohorts = imagingCart.map(i => i.cohort);
    const genomicsCohorts = genomicsCart.map(i => i.cohort);
    return [...new Set([...phenotpyicCohorts, ...imagingCohorts, ...genomicsCohorts])].sort();
  }, [cartByCohort, imagingCart, genomicsCart]);

  const categoryStats = useMemo(() => {
    const stats = {};
    cartItems.forEach(item => {
      stats[item.Category] = (stats[item.Category] || 0) + 1;
    });
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  }, [cartItems]);

  const directoryData = useMemo(() => {
    const grouped = {};
    data.forEach(item => {
      if (!grouped[item.Cohort]) {
        grouped[item.Cohort] = { total: 0, categories: {} };
      }
      grouped[item.Cohort].total += 1;
      if (!grouped[item.Cohort].categories[item.Category]) {
        grouped[item.Cohort].categories[item.Category] = [];
      }
      grouped[item.Cohort].categories[item.Category].push(item);
    });
    return grouped;
  }, [data]);

  // HANDLERS


  const getHeatmapColor = (value, maxVal) => {
    if (!value) return 'bg-slate-50 text-slate-300';

    // Using a logarithmic scale to handle the skew (where few cells have thousands of vars 
    // and most have 10s-100s). This spreads the color spectrum more effectively.
    const logValue = Math.log10(value + 1);
    const logMax = Math.log10(maxVal + 1);
    const ratio = logValue / logMax;

    if (ratio > 0.8) return 'bg-purple-900 text-white shadow-inner';
    if (ratio > 0.6) return 'bg-purple-700 text-white';
    if (ratio > 0.4) return 'bg-purple-500 text-white';
    if (ratio > 0.2) return 'bg-purple-300 text-purple-900';
    return 'bg-purple-100 text-purple-800';
  };

  const toggleCartItem = (id) => {
    const isAdding = !cart.includes(id);
    setCart(prev => isAdding ? [...prev, id] : prev.filter(itemId => itemId !== id));

    if (isAdding) {
      const variable = data.find(d => d.id === id);
      if (variable) {
        showRecPopup(variable);
      }
    }
  };

  const showRecPopup = async (variable) => {
    setRecPopup({ visible: true, sourceVar: variable, recommendations: [] });
    setIsRecPopupLoading(true);

    // Auto-hide after 8 seconds if no interaction
    const timer = setTimeout(() => {
      // We don't want to hide if it's currently being interacted with, but for simplicity:
      // setRecPopup(prev => ({ ...prev, visible: false }));
    }, 8000);

    try {
      const assets = await initSemanticAssets();
      const dims = 384;
      const vId = variable.id;
      const vIdx = assets.embeddingIds.indexOf(vId);

      let queryVector;
      if (vIdx !== -1) {
        queryVector = assets.embeddingsArray.slice(vIdx * dims, (vIdx + 1) * dims);
      } else {
        const output = await assets.extractor(variable['Variable Label'] || variable['Variable Name'], { pooling: 'mean', normalize: true });
        queryVector = output.data;
      }

      const results = [];
      for (let i = 0; i < assets.embeddingIds.length; i++) {
        const testId = assets.embeddingIds[i];
        if (testId === vId) continue;

        let dotProduct = 0;
        for (let j = 0; j < dims; j++) {
          dotProduct += queryVector[j] * assets.embeddingsArray[i * dims + j];
        }
        results.push({ id: testId, score: dotProduct });
      }

      results.sort((a, b) => b.score - a.score);
      const relevant = results
        .filter(r => r.score > 0.6)
        .slice(0, 50)
        .map(r => ({
          ...data.find(d => d.id === r.id),
          similarityScore: r.score
        })).filter(d => Boolean(d.id));

      setRecPopup(prev => ({ ...prev, recommendations: relevant }));
    } catch (e) {
      console.error("Rec popup failed", e);
    } finally {
      setIsRecPopupLoading(false);
    }
  };

  const toggleImagingScan = (cohort, type) => {
    setImagingCart(prev => {
      const exists = prev.find(item => item.cohort === cohort && item.type === type);
      if (exists) {
        return prev.filter(item => !(item.cohort === cohort && item.type === type));
      } else {
        return [...prev, { cohort, type }];
      }
    });
  };

  const toggleGenomicsScan = (cohort, type) => {
    setGenomicsCart(prev => {
      const exists = prev.find(item => item.cohort === cohort && item.type === type);
      if (exists) {
        return prev.filter(item => !(item.cohort === cohort && item.type === type));
      } else {
        return [...prev, { cohort, type }];
      }
    });
  };

  const toggleGroupCart = (variables) => {
    const varIds = variables.map(v => v.id);
    const allInCart = varIds.every(id => cart.includes(id));
    if (allInCart) {
      setCart(cart.filter(id => !varIds.includes(id)));
    } else {
      const idsToAdd = varIds.filter(id => !cart.includes(id));
      setCart([...cart, ...idsToAdd]);
    }
  };

  const removeCohortFromCart = (cohortName) => {
    const variablesInCohort = data.filter(v => v.Cohort === cohortName).map(v => v.id);
    setCart(cart.filter(id => !variablesInCohort.includes(id)));
  };

  const toggleCartCohortExpand = (cohortName) => {
    setExpandedCartCohorts(prev => ({
      ...prev,
      [cohortName]: prev[cohortName] === false ? true : false
    }));
  };

  const toggleCategoryFilter = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const toggleGroupExpand = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const toggleDirCohort = (cohort) => {
    setExpandedDirCohorts(prev => ({ ...prev, [cohort]: !prev[cohort] }));
  };

  const handleMatrixSort = (cat) => {
    setMatrixSort(prev => {
      if (prev.column === cat) {
        return { column: cat, direction: prev.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { column: cat, direction: 'desc' };
    });
  };

  const toggleDirCategory = (cohort, category) => {
    setActiveDirCategory({ cohort, category });
  };

  const handleAiSearch = async () => {
    if (!aiSearchQuery.trim()) { setAiSearchResults(null); setAiSearchError(null); return; }
    setIsAiSearching(true);
    setAiSearchError(null);

    const cohortData = data.filter(d => d.Cohort === selectedCohort);

    try {
      const assets = await initSemanticAssets();
      const output = await assets.extractor(aiSearchQuery, { pooling: 'mean', normalize: true });
      const queryVector = output.data;
      const dims = 384;

      const validVars = cohortData
        .map(v => ({ ...v, embIdx: assets.embeddingIds.indexOf(v.id) }))
        .filter(v => v.embIdx !== -1);

      const results = [];
      for (const v of validVars) {
        let dotProduct = 0;
        const offset = v.embIdx * dims;
        for (let j = 0; j < dims; j++) {
          dotProduct += queryVector[j] * assets.embeddingsArray[offset + j];
        }
        results.push({ id: v.id, score: dotProduct });
      }

      results.sort((a, b) => b.score - a.score);
      const topResults = results.filter(r => r.score > 0.3).slice(0, 100);
      const matchedIds = topResults.map(r => r.id);

      setAiSearchResults(matchedIds);
    } catch (err) {
      console.error(err);
      setAiSearchError("Failed to perform AI search.");
    } finally {
      setIsAiSearching(false);
    }
  };

  const initSemanticAssets = async () => {
    if (semanticAssets) return semanticAssets;
    try {
      setSemanticModelLoading(true);
      const { pipeline, env } = await import('@xenova/transformers');
      env.allowLocalModels = false;
      const extractor = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2', {
        quantized: true,
      });
      const binResponse = await fetch(`${process.env.PUBLIC_URL}/metadata/embeddings.bin`);
      const binBuffer = await binResponse.arrayBuffer();
      const embeddingsArray = new Float32Array(binBuffer);

      const jsonResponse = await fetch(`${process.env.PUBLIC_URL}/metadata/embedding_ids.json`);
      const embeddingIds = await jsonResponse.json();

      const assets = { extractor, embeddingsArray, embeddingIds };
      setSemanticAssets(assets);
      setSemanticModelLoading(false);
      return assets;
    } catch (err) {
      console.error(err);
      setSemanticModelLoading(false);
      throw new Error("Failed to initialize AI embedding model or load vectors.");
    }
  };

  // Pre-load semantic assets in the background on app load
  useEffect(() => {
    initSemanticAssets().catch(err => console.warn("Background semantic load failed:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGlobalSemanticSearch = async () => {
    if (!globalSearchQuery.trim()) {
      setGlobalSearchResults(null);
      setGlobalSearchError(null);
      return;
    }
    setIsGlobalSearching(true);
    setGlobalSearchError(null);
    try {
      const assets = await initSemanticAssets();

      const output = await assets.extractor(globalSearchQuery, { pooling: 'mean', normalize: true });
      const queryVector = output.data;

      const numEmbeddings = assets.embeddingIds.length;
      const dims = 384;
      const results = [];

      for (let i = 0; i < numEmbeddings; i++) {
        let dotProduct = 0;
        for (let j = 0; j < dims; j++) {
          dotProduct += queryVector[j] * assets.embeddingsArray[i * dims + j];
        }
        results.push({ id: assets.embeddingIds[i], score: dotProduct });
      }

      results.sort((a, b) => b.score - a.score);
      const topResults = results.slice(0, 100).filter(r => r.score > 0.3);

      const resultItems = topResults.map(r => ({
        ...data.find(d => d.id === r.id),
        similarityScore: r.score
      })).filter(d => Boolean(d.id));

      setGlobalSearchResults(resultItems);
    } catch (err) {
      setGlobalSearchError("Failed to perform global search. " + err.message);
    } finally {
      setIsGlobalSearching(false);
    }
  };

  const handleResearchSearch = async () => {
    if (!researchQuestion.trim()) {
      setResearchCohortResults(null);
      setResearchSearchError(null);
      return;
    }
    setIsResearchSearching(true);
    setResearchSearchError(null);

    try {
      const assets = await initSemanticAssets();
      const output = await assets.extractor(researchQuestion, { pooling: 'mean', normalize: true });
      const queryVector = output.data;

      const dims = 384;
      const numEmbeddings = assets.embeddingIds.length;

      const cohortScores = {};

      for (let i = 0; i < numEmbeddings; i++) {
        let dotProduct = 0;
        for (let j = 0; j < dims; j++) {
          dotProduct += queryVector[j] * assets.embeddingsArray[i * dims + j];
        }

        if (dotProduct > 0.35) { // Threshold for relevance
          const varId = assets.embeddingIds[i];
          const v = data.find(d => d.id === varId);
          if (v && v.Cohort) {
            if (!cohortScores[v.Cohort]) cohortScores[v.Cohort] = { totalScore: 0, count: 0 };
            cohortScores[v.Cohort].totalScore += dotProduct;
            cohortScores[v.Cohort].count += 1;
          }
        }
      }

      const results = Object.keys(cohortScores)
        .map(cohort => ({
          cohort,
          score: cohortScores[cohort].totalScore,
          matchedVars: cohortScores[cohort].count
        }))
        .filter(r => r.matchedVars >= 3)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);

      setResearchCohortResults(results);
    } catch (err) {
      console.error(err);
      setResearchSearchError("Failed to search cohorts.");
    } finally {
      setIsResearchSearching(false);
    }
  };

  const handleFindSimilarCart = async (variable) => {
    setSimilarOriginVar(variable);
    setSimilarVars([]);
    setSimilarModalOpen(true);
    setIsSimilarLoading(true);

    try {
      const assets = await initSemanticAssets();

      const vId = variable.id;
      const vIdx = assets.embeddingIds.indexOf(vId);

      let queryVector;
      const dims = 384;

      if (vIdx !== -1) {
        queryVector = assets.embeddingsArray.slice(vIdx * dims, (vIdx + 1) * dims);
      } else {
        const output = await assets.extractor(variable['Variable Label'] || variable['Variable Name'], { pooling: 'mean', normalize: true });
        queryVector = output.data;
      }

      const numEmbeddings = assets.embeddingIds.length;
      const results = [];

      for (let i = 0; i < numEmbeddings; i++) {
        if (assets.embeddingIds[i] === vId) continue;

        let dotProduct = 0;
        for (let j = 0; j < dims; j++) {
          dotProduct += queryVector[j] * assets.embeddingsArray[i * dims + j];
        }
        results.push({ id: assets.embeddingIds[i], score: dotProduct });
      }

      results.sort((a, b) => b.score - a.score);
      const relevantResults = results.filter(r => r.score > 0.6).slice(0, 50);

      const resultItems = relevantResults.map(r => ({
        ...data.find(d => d.id === r.id),
        similarityScore: r.score
      })).filter(d => Boolean(d.id));

      setSimilarVars(resultItems);
    } catch (err) {
      console.error("Failed to find similar variables", err);
    } finally {
      setIsSimilarLoading(false);
    }
  };

  const handleCalculateOverlap = async () => {
    if (!overlapCohortA || !overlapCohortB) return;
    setIsOverlapCalculating(true);
    setOverlapError(null);
    setOverlapResults(null);

    try {
      const assets = await initSemanticAssets();
      const dims = 384;

      const isIdVar = (v) => {
        const name = (v['Variable Name'] || '').toLowerCase();
        const label = (v['Variable Label'] || '').toLowerCase();
        return name === 'id' ||
          name.includes('participant_id') ||
          name.includes('subject_id') ||
          label.toLowerCase().includes('participant id') ||
          label.toLowerCase().includes('subject id') ||
          label.toLowerCase().includes('unique identifier');
      };

      const varsA = data
        .filter(d => d.Cohort === overlapCohortA && !isIdVar(d))
        .map(v => ({ ...v, embIdx: assets.embeddingIds.indexOf(v.id) }))
        .filter(v => v.embIdx !== -1);

      const varsB = data
        .filter(d => d.Cohort === overlapCohortB && !isIdVar(d))
        .map(v => ({ ...v, embIdx: assets.embeddingIds.indexOf(v.id) }))
        .filter(v => v.embIdx !== -1);

      if (varsA.length === 0 || varsB.length === 0) {
        setOverlapResults({ pairs: [], totalA: varsA.length, totalB: varsB.length });
        return;
      }

      const results = [];

      for (const varA of varsA) {
        const offsetA = varA.embIdx * dims;
        let bestScore = -1;
        let bestVarB = null;

        for (const varB of varsB) {
          const offsetB = varB.embIdx * dims;
          let dotProduct = 0;
          for (let j = 0; j < dims; j++) {
            dotProduct += assets.embeddingsArray[offsetA + j] * assets.embeddingsArray[offsetB + j];
          }
          if (dotProduct > bestScore) {
            bestScore = dotProduct;
            bestVarB = varB;
          }
        }

        if (bestScore >= overlapThreshold) {
          results.push({ varA, varB: bestVarB, score: bestScore });
        }
      }

      results.sort((a, b) => b.score - a.score);
      setOverlapResults({ pairs: results, totalA: varsA.length, totalB: varsB.length });

    } catch (err) {
      console.error(err);
      setOverlapError("Failed to calculate semantic overlap.");
    } finally {
      setIsOverlapCalculating(false);
    }
  };

  const handleCalculateNetwork = async () => {
    setIsNetworkCalculating(true);
    setNetworkError(null);
    try {
      const assets = await initSemanticAssets();
      const dims = 384;
      const threshold = 0.75;

      const cohortGroups = {};
      allCohorts.forEach(c => {
        cohortGroups[c] = data
          .filter(d => d.Cohort === c)
          .map(v => ({ ...v, embIdx: assets.embeddingIds.indexOf(v.id) }))
          .filter(v => v.embIdx !== -1);
      });

      const nodes = allCohorts.map(c => ({
        id: c,
        count: cohortGroups[c].length,
        x: 500 + (Math.random() - 0.5) * 100,
        y: 400 + (Math.random() - 0.5) * 100,
        vx: 0,
        vy: 0
      })).filter(n => n.count > 0);

      const links = [];
      const cohortNames = nodes.map(n => n.id);

      for (let i = 0; i < cohortNames.length; i++) {
        const cA = cohortNames[i];
        const varsA = cohortGroups[cA];
        const sampleA = varsA.length > 200 ? varsA.slice(0, 200) : varsA;

        for (let j = i + 1; j < cohortNames.length; j++) {
          const cB = cohortNames[j];
          const varsB = cohortGroups[cB];
          const sampleB = varsB.length > 200 ? varsB.slice(0, 200) : varsB;

          let matches = 0;
          for (const varA of sampleA) {
            const offsetA = varA.embIdx * dims;
            for (const varB of sampleB) {
              const offsetB = varB.embIdx * dims;
              let dotProduct = 0;
              for (let k = 0; k < dims; k++) {
                dotProduct += assets.embeddingsArray[offsetA + k] * assets.embeddingsArray[offsetB + k];
              }
              if (dotProduct >= threshold) {
                matches++;
                break;
              }
            }
          }

          if (matches > 1) {
            links.push({ source: cA, target: cB, value: matches });
          }
        }
      }

      const center = { x: 500, y: 400 };
      const preWarmIterations = 150;
      const repulsionStrength = 55000;
      const springBack = 0.001;

      for (let iter = 0; iter < preWarmIterations; iter++) {
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dx = nodes[j].x - nodes[i].x;
            const dy = nodes[j].y - nodes[i].y;
            const distSq = dx * dx + dy * dy || 1;
            const dist = Math.sqrt(distSq);
            const force = Math.min(100, repulsionStrength / Math.max(distSq, 500));
            const fx = (dx / dist) * force;
            const fy = (dy / dist) * force;
            nodes[i].vx -= fx;
            nodes[i].vy -= fy;
            nodes[j].vx += fx;
            nodes[j].vy += fy;
          }
        }

        const nodeMap = {};
        nodes.forEach((n, idx) => { nodeMap[n.id] = idx; });

        links.forEach(link => {
          const sIdx = nodeMap[link.source];
          const tIdx = nodeMap[link.target];
          if (sIdx === undefined || tIdx === undefined) return;
          const s = nodes[sIdx];
          const t = nodes[tIdx];
          const dx = t.x - s.x;
          const dy = t.y - s.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const strength = Math.min(0.05, link.value * 0.001);
          const idealDist = 650;
          const force = (dist - idealDist) * strength;
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          nodes[sIdx].vx += fx;
          nodes[sIdx].vy += fy;
          nodes[tIdx].vx -= fx;
          nodes[tIdx].vy -= fy;
        });

        nodes.forEach(n => {
          n.vx += (center.x - n.x) * springBack;
          n.vy += (center.y - n.y) * springBack;
          n.vx = Math.max(-20, Math.min(20, n.vx));
          n.vy = Math.max(-20, Math.min(20, n.vy));
          n.x += n.vx;
          n.y += n.vy;
          n.vx *= 0.75;
          n.vy *= 0.75;
        });
      }

      setNetworkData({ nodes, links });
    } catch (err) {
      console.error(err);
      setNetworkError("Failed to calculate study connections.");
    } finally {
      setIsNetworkCalculating(false);
    }
  };

  const handleDownloadCSV = () => {
    if (cartItems.length === 0 && imagingCart.length === 0 && genomicsCart.length === 0) return;

    const safeProject = projectNumber.trim().replace(/[^a-z0-9-]/gi, '_') || 'project';
    const safeUser = userName.trim().replace(/[^a-z0-9-]/gi, '_') || 'user';
    const date = new Date().toISOString().split('T')[0];
    const filename = `${safeProject}_${safeUser}_${date}_selected_variables.csv`.toLowerCase();

    const headers = ['Type', 'Cohort', 'Category', 'Filename', 'Variable Name', 'Variable Label', 'Data Type', 'Completeness'];

    // Rows for phenotypic variables
    const varRows = cartItems.map(item => [
      '"Variable"',
      `"${item.Cohort}"`,
      `"${item.Category}"`,
      `"${item.Filename}"`,
      `"${item['Variable Name']}"`,
      `"${(item['Variable Label'] || '').replace(/"/g, '""')}"`,
      `"${item['Data Type']}"`,
      `"${item.Completeness}"`
    ]);

    // Rows for imaging scans
    const imgRows = imagingCart.map(item => [
      `"Imaging Scan (${item.type})"`,
      `"${item.cohort}"`,
      '"Imaging"',
      '"N/A"',
      '"N/A"',
      '"N/A"',
      '"N/A"',
      '"N/A"'
    ]);

    // Rows for genomics
    const genRows = genomicsCart.map(item => [
      `"Genomics Modality (${item.type})"`,
      `"${item.cohort}"`,
      '"Genomics"',
      '"N/A"',
      '"N/A"',
      '"N/A"',
      '"N/A"',
      '"N/A"'
    ]);

    const csvContent = [headers.join(','), ...varRows.map(r => r.join(',')), ...imgRows.map(r => r.join(',')), ...genRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // VIEWS
  const renderCohortView = () => {
    const cohortData = data.filter(d => d.Cohort === selectedCohort);
    let filteredData = cohortData;
    if (aiSearchResults !== null) filteredData = filteredData.filter(d => aiSearchResults.includes(d.id));
    if (searchQuery) filteredData = filteredData.filter(d => d['Variable Name'].toLowerCase().includes(searchQuery.toLowerCase()) || d['Variable Label'].toLowerCase().includes(searchQuery.toLowerCase()));
    const groupedData = filteredData.reduce((acc, curr) => {
      const key = curr[groupBy] || 'Unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(curr);
      return acc;
    }, {});

    return (
      <div className="flex flex-col h-full bg-slate-50">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-10 shadow-sm">
          <div className="flex items-center gap-4">
            <button onClick={() => setCurrentView('matrix')} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500 hover:text-purple-600"><ArrowLeft className="w-5 h-5" /></button>
            <div><h1 className="text-2xl font-bold text-slate-800">{selectedCohort}</h1><p className="text-sm text-slate-500">{cohortData.length} total variables</p></div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setExpandedGroups({})}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                title="Expand All"
              >
                <Maximize2 className="w-3.5 h-3.5" /> Expand All
              </button>
              <button
                onClick={() => {
                  const collapsed = {};
                  Object.keys(groupedData).forEach(key => collapsed[key] = false);
                  setExpandedGroups(collapsed);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                title="Collapse All"
              >
                <Minimize2 className="w-3.5 h-3.5" /> Collapse All
              </button>
            </div>

            <div className="flex items-center border-l border-slate-200 pl-4 ml-1">
              <div className="flex bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => setGroupBy('Category')}
                  className={classNames("px-4 py-1.5 text-sm font-medium rounded-md transition-all", groupBy === 'Category' ? "bg-white text-purple-700 shadow-sm" : "text-slate-500 hover:text-slate-700")}
                >
                  By Category
                </button>
                <button
                  onClick={() => setGroupBy('Filename')}
                  className={classNames("px-4 py-1.5 text-sm font-medium rounded-md transition-all", groupBy === 'Filename' ? "bg-white text-purple-700 shadow-sm" : "text-slate-500 hover:text-slate-700")}
                >
                  By Table Name
                </button>
              </div>
            </div>

            <div className="relative group ml-1">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-purple-500 group-focus-within:text-purple-600 transition-colors" />
              <input
                type="text"
                placeholder="Search variables in this cohort..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-12 py-2.5 border-2 border-purple-500 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-purple-500/10 w-96 bg-white text-slate-900 placeholder-slate-400 hover:border-purple-600 focus:border-purple-700 shadow-sm transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-600 transition-colors bg-purple-50 hover:bg-purple-100 rounded-full p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="p-6 max-w-7xl mx-auto w-full flex-1 overflow-y-auto">
          <div className="mb-8 bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue rounded-2xl p-6 shadow-md text-white">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-pink-200" /><h2 className="text-xl font-semibold">AI Semantic Search</h2></div>
            <p className="text-pink-100 text-sm mb-5">Describe concepts to find relevant variables across this cohort.</p>
            <div className="relative flex items-center"><input type="text" placeholder="E.g., variables related to sleep patterns..." value={aiSearchQuery} onChange={(e) => setAiSearchQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAiSearch(); }} disabled={isAiSearching} className="w-full bg-white/10 border border-white/20 text-white placeholder-white/60 rounded-xl pl-4 pr-24 py-3 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm transition-all shadow-inner disabled:opacity-50" /><div className="absolute right-2 flex items-center gap-1">{aiSearchQuery && <button onClick={() => { setAiSearchQuery(''); setAiSearchResults(null); setAiSearchError(null); }} className="p-2 text-white/70 hover:text-white transition-colors"><X className="w-4 h-4" /></button>}<button onClick={handleAiSearch} disabled={isAiSearching} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50">{isAiSearching ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Search className="w-4 h-4 text-white" />}</button></div></div>
            {aiSearchError && <p className="text-red-200 text-sm mt-3 bg-red-900/30 p-2 rounded border border-red-500/30">{aiSearchError}</p>}
            {aiSearchResults !== null && !isAiSearching && !aiSearchError && <div className="mt-4 flex items-center justify-between bg-white/10 p-3 rounded-lg border border-white/20"><p className="text-white text-sm flex items-center gap-2"><Check className="w-5 h-5 text-green-300" /><span className="font-medium">AI found {aiSearchResults.length} relevant variables.</span></p><button onClick={() => { setAiSearchQuery(''); setAiSearchResults(null); setAiSearchError(null); }} className="text-xs font-medium bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-md transition-colors">Clear Results</button></div>}
          </div>
          {Object.keys(groupedData).sort().map(groupName => {
            const groupVars = groupedData[groupName];
            const isExpanded = expandedGroups[groupName] !== false;
            const allInCart = groupVars.every(v => cart.includes(v.id));
            const groupId = `cat-${groupName.replace(/\s+/g, '-')}`;
            return (
              <div key={groupName} id={groupId} className="mb-4 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden scroll-mt-20">
                <div className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleGroupExpand(groupName)}><div className="flex items-center gap-3">{isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}<div className="flex items-center gap-2">{groupBy === 'Category' ? <Layers className="w-5 h-5 text-purple-500" /> : <FileText className="w-5 h-5 text-purple-500" />}<h2 className="text-lg font-semibold text-slate-800">{groupName}</h2></div><span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">{groupVars.length} variables</span></div><button onClick={(e) => { e.stopPropagation(); toggleGroupCart(groupVars); }} className={classNames("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all", allInCart ? "bg-purple-100 text-purple-700 hover:bg-purple-200" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50")}>{allInCart ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}{allInCart ? 'All Added' : 'Add Group to Cart'}</button></div>
                {isExpanded && <div className="border-t border-slate-100 overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 text-slate-500 border-b border-slate-100"><tr><th className="px-6 py-3 font-medium w-12"></th><th className="px-6 py-3 font-medium">Variable Name</th><th className="px-6 py-3 font-medium">Label</th><th className="px-6 py-3 font-medium">Data Type</th><th className="px-6 py-3 font-medium">Completeness</th></tr></thead><tbody className="divide-y divide-slate-100">{groupVars.map(variable => { const isSelected = cart.includes(variable.id); const compData = parseCompleteness(variable.Completeness); return (<tr key={variable.id} className={classNames("hover:bg-purple-50/50 transition-colors cursor-pointer", isSelected ? "bg-purple-50/30" : "")} onClick={() => toggleCartItem(variable.id)}><td className="px-6 py-3"><div className={classNames("w-5 h-5 rounded border flex items-center justify-center transition-colors", isSelected ? "bg-purple-600 border-purple-600 text-white" : "border-slate-300 bg-white")}>{isSelected && <Check className="w-3.5 h-3.5" />}</div></td><td className="px-6 py-3 font-medium text-slate-900 font-mono text-xs">{variable['Variable Name']}</td><td className="px-6 py-3 text-slate-600">{variable['Variable Label']}</td><td className="px-6 py-3"><span className="px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs">{variable['Data Type']}</span></td><td className="px-6 py-3"><div className="flex items-center gap-2"><div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden"><div className={classNames("h-full transition-all duration-300", compData.color)} style={{ width: `${compData.percent}%` }} /></div><span className="text-xs text-slate-500 w-10">{compData.text}</span></div></td></tr>); })}</tbody></table></div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCartView = () => {
    return (
      <div className="flex flex-col h-full bg-slate-50">
        <div className="bg-white border-b px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => setCurrentView('matrix')} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500 hover:text-purple-600"><ArrowLeft className="w-5 h-5" /></button>
            <div><h1 className="text-2xl font-bold text-slate-800">Research Cart</h1><div className="flex items-center gap-4 mt-0.5"><div className="flex items-center gap-2"><p className="text-sm text-slate-500"><span className="font-bold text-slate-700">{cart.length}</span> variables</p></div><span className="text-slate-300">|</span><div className="flex items-center gap-2"><p className="text-sm text-slate-500"><span className="font-bold text-slate-700">{imagingCart.length}</span> imaging</p></div><span className="text-slate-300">|</span><div className="flex items-center gap-2"><p className="text-sm text-slate-500"><span className="font-bold text-slate-700">{genomicsCart.length}</span> genomics</p></div><span className="text-slate-300">|</span><p className="text-sm text-slate-500"><span className="font-bold text-slate-700">{cartCohorts.length}</span> cohorts</p></div></div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  setIsImagingCartExpanded(true);
                  setIsGenomicsCartExpanded(true);
                  setExpandedCartCohorts({}); // Empty object means everything expanded default
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                title="Expand All"
              >
                <Maximize2 className="w-3.5 h-3.5" /> Expand All
              </button>
              <button
                onClick={() => {
                  setIsImagingCartExpanded(false);
                  setIsGenomicsCartExpanded(false);
                  const collapsed = {};
                  Object.keys(cartByCohort).forEach(cohort => collapsed[cohort] = false);
                  setExpandedCartCohorts(collapsed);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                title="Collapse All"
              >
                <Minimize2 className="w-3.5 h-3.5" /> Collapse All
              </button>
            </div>
            <div className="h-6 w-px bg-slate-200 mx-1"></div>
            <button onClick={() => { setCart([]); setImagingCart([]); setGenomicsCart([]); }} className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-red-600 transition-colors flex items-center gap-2"><Trash2 className="w-4 h-4" />Clear All</button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto w-full flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-8 p-6">
          {/* Main List Column */}
          <div className="lg:col-span-8 flex flex-col gap-6 overflow-y-auto pr-2 custom-scrollbar">
            {imagingCart.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500">
                <div
                  className="bg-pink-50/50 px-6 py-4 border-b border-slate-200 flex justify-between items-center cursor-pointer hover:bg-pink-100/50 transition-colors"
                  onClick={() => setIsImagingCartExpanded(!isImagingCartExpanded)}
                >
                  <div className="flex items-center gap-3">
                    {isImagingCartExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                    <div className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center text-pink-600 shadow-sm"><Brain className="w-4 h-4" /></div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-sm">Selected Imaging Scans</h3>
                      <p className="text-[11px] text-slate-500">{imagingCart.length} scans across {[...new Set(imagingCart.map(i => i.cohort))].length} cohorts</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setImagingCart([]); }}
                    className="text-[11px] font-bold text-slate-400 hover:text-red-600 transition-colors uppercase tracking-wider"
                  >
                    Remove All Scans
                  </button>
                </div>
                {isImagingCartExpanded && (
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/30 animate-in fade-in slide-in-from-top-2 duration-300">
                    {imagingCart.map((item, idx) => (
                      <div key={`${item.cohort}-${item.type}-${idx}`} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 group hover:border-pink-200 hover:shadow-sm transition-all">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-pink-500 shadow-[0_0_8px_rgba(236,72,153,0.5)]" />
                          <div className="min-w-0">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest truncate">{item.cohort}</p>
                            <p className="text-sm font-bold text-slate-700 truncate">{item.type}</p>
                          </div>
                        </div>
                        <button onClick={() => toggleImagingScan(item.cohort, item.type)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Remove scan"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {genomicsCart.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500">
                <div
                  className="bg-indigo-50/50 px-6 py-4 border-b border-slate-200 flex justify-between items-center cursor-pointer hover:bg-indigo-100/50 transition-colors"
                  onClick={() => setIsGenomicsCartExpanded(!isGenomicsCartExpanded)}
                >
                  <div className="flex items-center gap-3">
                    {isGenomicsCartExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                    <div className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center text-indigo-600 shadow-sm"><Dna className="w-4 h-4" /></div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-sm">Selected Genomics Scans</h3>
                      <p className="text-[11px] text-slate-500">{genomicsCart.length} modalities across {[...new Set(genomicsCart.map(i => i.cohort))].length} cohorts</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setGenomicsCart([]); }}
                    className="text-[11px] font-bold text-slate-400 hover:text-red-600 transition-colors uppercase tracking-wider"
                  >
                    Remove All Genomics
                  </button>
                </div>
                {isGenomicsCartExpanded && (
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/30 animate-in fade-in slide-in-from-top-2 duration-300">
                    {genomicsCart.map((item, idx) => (
                      <div key={`${item.cohort}-${item.type}-${idx}`} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 group hover:border-indigo-200 hover:shadow-sm transition-all">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                          <div className="min-w-0">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest truncate">{item.cohort}</p>
                            <p className="text-sm font-bold text-slate-700 truncate">{item.type}</p>
                          </div>
                        </div>
                        <button onClick={() => toggleGenomicsScan(item.cohort, item.type)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Remove modality"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {cart.length === 0 && imagingCart.length === 0 && genomicsCart.length === 0 ? (
              <div className="text-center py-32 bg-white rounded-2xl border border-dashed border-slate-300">
                <ShoppingCart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-700 mb-2">Your cart is empty</h2>
                <p className="text-slate-500 mb-6">Explore cohorts and add variables, imaging, or genomics to build your research dataset.</p>
                <button onClick={() => setCurrentView('matrix')} className="px-6 py-2 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-lg font-bold transition-colors">Start Discovery</button>
              </div>
            ) : (
              Object.keys(cartByCohort).sort().map(cohort => {
                const isExpanded = expandedCartCohorts[cohort] !== false;

                return (
                  <div key={cohort} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden line-clamp-none">
                    <div
                      className="bg-slate-50/50 px-6 py-4 border-b border-slate-200 flex justify-between items-center cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => toggleCartCohortExpand(cohort)}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                        <div className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center text-purple-600 shadow-sm"><Database className="w-4 h-4" /></div>
                        <div>
                          <h3 className="font-bold text-slate-800">{cohort}</h3>
                          <p className="text-xs text-slate-500">{cartByCohort[cohort].length} variables selected</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeCohortFromCart(cohort); }}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
                        title={`Remove all ${cohort} variables from cart`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove Cohort
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="divide-y divide-slate-100">
                        {cartByCohort[cohort].map(variable => {
                          const compData = parseCompleteness(variable.Completeness);
                          return (
                            <div key={variable.id} className="p-4 px-6 hover:bg-slate-50 transition-colors flex items-center gap-4 group">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono text-[11px] font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded uppercase">{variable['Variable Name']}</span>
                                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{variable.Category}</span>
                                </div>
                                <p className="text-sm text-slate-600 truncate font-medium">{variable['Variable Label']}</p>
                                <div className="flex items-center gap-4 mt-2">
                                  <div className="flex items-center gap-1.5 text-xs text-slate-400"><Layers className="w-3 h-3" /><span>{variable['Data Type']}</span></div>
                                  <div className="flex items-center gap-2">
                                    <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden"><div className={classNames("h-full", compData.color)} style={{ width: `${compData.percent}%` }} /></div>
                                    <span className="text-[10px] text-slate-400 font-medium">{compData.text} Complete</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => handleFindSimilarCart(variable)}
                                  title="Find Similar Variables using AI"
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-600 hover:text-white rounded-lg transition-all border border-purple-100 hover:border-purple-600 shadow-sm"
                                >
                                  <Sparkles className="w-3.5 h-3.5" />
                                  <span className="text-[10px] font-bold uppercase tracking-wider">Find Similar</span>
                                </button>
                                <button
                                  onClick={() => toggleCartItem(variable.id)}
                                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                  title="Remove from cart"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Sidebar Column */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            {/* Request Summary */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4 uppercase tracking-wider"><ClipboardList className="w-4 h-4 text-purple-600" />Request Summary</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100 shadow-sm">
                  <div className="flex items-center gap-2 text-purple-700"><FileText className="w-4 h-4" /><span className="text-sm font-medium">Variables</span></div>
                  <span className="font-bold text-purple-800">{cart.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg border border-pink-100 shadow-sm">
                  <div className="flex items-center gap-2 text-pink-700"><Brain className="w-4 h-4" /><span className="text-sm font-medium">Imaging</span></div>
                  <span className="font-bold text-pink-800">{imagingCart.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100 shadow-sm">
                  <div className="flex items-center gap-2 text-indigo-700"><Dna className="w-4 h-4" /><span className="text-sm font-medium">Genomics</span></div>
                  <span className="font-bold text-indigo-800">{genomicsCart.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-center gap-2 text-slate-600"><Database className="w-4 h-4" /><span className="text-sm">Total Cohorts</span></div>
                  <span className="font-bold text-purple-700">{cartCohorts.length}</span>
                </div>

                {categoryStats.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2"><PieChart className="w-3 h-3" /> Category Breakdown</h4>
                    <div className="space-y-3">
                      {categoryStats.map(([cat, count]) => (
                        <div key={cat}>
                          <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{cat}</span><span className="font-semibold">{count}</span></div>
                          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-purple-400" style={{ width: `${(count / cart.length) * 100}%` }} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mock Project Config */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-4 uppercase tracking-wider"><Briefcase className="w-4 h-4 text-purple-600" />Project Details</h3>
              <div className="space-y-4">
                <div><label className="text-[11px] font-bold text-slate-400 uppercase mb-1.5 block">User Name</label><input type="text" placeholder="Full Name" value={userName} onChange={(e) => setUserName(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none transition-all" /></div>
                <div><label className="text-[11px] font-bold text-slate-400 uppercase mb-1.5 block">Project Number</label><input type="text" placeholder="e.g. 0123" value={projectNumber} onChange={(e) => setProjectNumber(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none transition-all" /></div>
              </div>
            </div>

            <div className="bg-purple-900 rounded-xl p-6 text-white shadow-lg shadow-purple-200">
              <h4 className="font-bold mb-2 flex items-center gap-2"><Download className="w-4 h-4" /> Export Variables</h4>
              <p className="text-purple-100 text-xs leading-relaxed mb-4">
                {(cart.length > 0 || imagingCart.length > 0 || genomicsCart.length > 0) && (!userName.trim() || !projectNumber.trim())
                  ? "Please enter your Name and Project Number to enable export."
                  : "Export your selection (variables, imaging, and genomics) as a CSV file for your data access request."}
              </p>
              <button
                onClick={handleDownloadCSV}
                disabled={(cart.length === 0 && imagingCart.length === 0 && genomicsCart.length === 0) || !userName.trim() || !projectNumber.trim()}
                className="w-full py-3 bg-white text-purple-900 font-bold rounded-lg text-sm hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-80 disabled:cursor-not-allowed"
              >
                Download Selection (CSV)
              </button>
            </div>


          </div>
        </div>
      </div>
    );
  };

  const renderMatrixView = () => {
    const { matrix, maxVal } = matrixData;
    return (
      <div className="flex flex-col h-full bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm z-10 shrink-0">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8 mb-6">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <Table className="w-7 h-7 text-purple-600" /> Cohort Discovery Matrix
              </h1>
              <p className="text-slate-600 max-w-3xl mt-2">Explore available data across DPUK cohort studies for phenotypic, imaging and genomic data.</p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {(selectedCategories.length > 0 || matrixSort.column) && (
                <button
                  onClick={() => {
                    setSelectedCategories([]);
                    setMatrixSort({ column: null, direction: 'desc' });
                  }}
                  className="px-4 py-2 text-sm font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all border border-purple-100 whitespace-nowrap flex items-center gap-2 shadow-sm"
                >
                  <RotateCcw className="w-4 h-4" /> Reset View
                </button>
              )}
              <div className="flex p-1 bg-slate-100 rounded-xl border border-slate-200 shadow-inner">
                <button
                  onClick={() => setMatrixViewType('phenotypic')}
                  className={classNames(
                    "px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2",
                    matrixViewType === 'phenotypic' ? "bg-white text-purple-700 shadow-md" : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  <ClipboardList className="w-4 h-4" /> Phenotypic Matrix
                </button>
                <button
                  onClick={() => setMatrixViewType('imaging')}
                  className={classNames(
                    "px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2",
                    matrixViewType === 'imaging' ? "bg-white text-pink-700 shadow-md" : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  <Brain className="w-4 h-4" /> Imaging Matrix
                </button>
                <button
                  onClick={() => setMatrixViewType('genomics')}
                  className={classNames(
                    "px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2",
                    matrixViewType === 'genomics' ? "bg-white text-indigo-700 shadow-md" : "text-slate-500 hover:text-indigo-600"
                  )}
                >
                  <Dna className="w-4 h-4" /> Genomics Matrix
                </button>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100">
            {matrixViewType === 'phenotypic' ? (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-semibold text-slate-500 mr-2 flex items-center gap-1.5"><Filter className="w-4 h-4 text-purple-500" /> Filter by Required Categories:</span>
                {allCategories.map(cat => {
                  const isSelected = selectedCategories.includes(cat);
                  return (
                    <button key={cat} onClick={() => toggleCategoryFilter(cat)} className={classNames("px-4 py-1.5 rounded-full text-sm font-medium transition-all border", isSelected ? "bg-purple-600 border-purple-600 text-white shadow-sm" : "bg-white border-slate-300 text-slate-600 hover:border-purple-400 hover:text-purple-700 hover:bg-purple-50")}>
                      {cat}
                    </button>
                  );
                })}
              </div>
            ) : matrixViewType === 'imaging' ? (
              <div className="flex items-center gap-2.5 text-sm text-slate-500 font-medium bg-pink-50 border border-pink-100 px-4 py-2 rounded-xl animate-in fade-in slide-in-from-left-2 duration-500 shadow-sm w-fit">
                <Info className="w-4 h-4 text-pink-600" />
                <span>Select imaging scans to add them to your research cart.</span>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 text-sm text-slate-500 font-medium bg-indigo-50 border border-indigo-100 px-4 py-2 rounded-xl animate-in fade-in slide-in-from-left-2 duration-500 shadow-sm w-fit">
                <Info className="w-4 h-4 text-indigo-600" />
                <span>Select genomic file types to add them to your research cart.</span>
              </div>
            )}
          </div>
        </div>
        <div className="p-8 flex-1 overflow-auto">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden inline-block min-w-full">
            <table className="min-w-full divide-y divide-slate-200 text-sm text-left">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 font-semibold text-slate-700 w-64 sticky left-0 bg-slate-50 z-10 border-r border-slate-200 shadow-[1px_0_0_0_#e2e8f0]">Cohort</th>
                  {matrixViewType === 'phenotypic' ? allCategories.map(cat => {
                    const isSorted = matrixSort.column === cat;
                    return (
                      <th
                        key={cat}
                        className={classNames(
                          "px-6 py-4 font-medium text-center whitespace-nowrap min-w-[160px] cursor-pointer hover:bg-slate-100 transition-colors group/th",
                          isSorted ? "text-purple-700 bg-purple-50/50" : "text-slate-600"
                        )}
                        title={`Sort by ${cat}`}
                        onClick={() => handleMatrixSort(cat)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {cat}
                          <div className={classNames(
                            "transition-all",
                            isSorted ? "opacity-100" : "opacity-0 group-hover/th:opacity-50"
                          )}>
                            {isSorted && matrixSort.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                          </div>
                        </div>
                      </th>
                    );
                  }) : (matrixViewType === 'imaging' ? imagingMatrixData.headers : genomicsMatrixData.headers).map(header => (
                    <th
                      key={header}
                      className="px-6 py-4 font-medium text-center whitespace-nowrap min-w-[120px] text-slate-600"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {(matrixViewType === 'phenotypic' ? visibleCohorts : (matrixViewType === 'imaging' ? allImagingCohorts : allGenomicsCohorts)).map(cohort => (
                  <tr key={cohort} className={classNames("transition-colors group", matrixViewType === 'phenotypic' ? "hover:bg-purple-50/50" : "hover:bg-slate-50")}>
                    <td
                      className={classNames(
                        "px-6 py-4 font-medium sticky left-0 z-10 border-r border-slate-100 shadow-[1px_0_0_0_#f1f5f9] flex items-center justify-between",
                        matrixViewType === 'phenotypic' ? "cursor-pointer text-purple-900 bg-white group-hover:bg-purple-50/50" : "bg-white text-slate-700"
                      )}
                      onClick={() => {
                        if (matrixViewType === 'phenotypic') {
                          setSelectedCohort(cohort);
                          setGroupBy('Category');
                          setExpandedGroups({});
                          setSearchQuery('');
                          setAiSearchQuery('');
                          setAiSearchResults(null);
                          setAiSearchError(null);
                          setCurrentView('cohort');
                        }
                      }}
                    >
                      <span>{cohort}</span>
                      {matrixViewType === 'phenotypic' && <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-purple-500 opacity-0 group-hover:opacity-100 transition-all" />}
                    </td>
                    {matrixViewType === 'phenotypic' ? allCategories.map(cat => {
                      const count = matrix[cohort][cat] || 0;
                      return (
                        <td key={cat} className={classNames("p-1 text-center", count > 0 ? "cursor-pointer" : "")} onClick={() => { if (count > 0) { setSelectedCohort(cohort); setGroupBy('Category'); const newExp = {}; allCategories.forEach(c => newExp[c] = (c === cat)); setExpandedGroups(newExp); setLastTargetedCategory(cat); setSearchQuery(''); setAiSearchQuery(''); setAiSearchResults(null); setAiSearchError(null); setCurrentView('cohort'); } }}>
                          <div className={classNames("w-full h-10 rounded-md flex items-center justify-center font-medium transition-all group-hover:opacity-90", count > 0 && "hover:ring-2 hover:ring-purple-400 hover:ring-offset-1", getHeatmapColor(count, maxVal))}>{count > 0 ? count : '-'}</div>
                        </td>
                      );
                    }) : (matrixViewType === 'imaging' ? imagingMatrixData.headers : genomicsMatrixData.headers).map(header => {
                      const isCollected = (matrixViewType === 'imaging' ? imagingMatrixData : genomicsMatrixData).data[cohort]?.[header];
                      const isInCart = (matrixViewType === 'imaging' ? imagingCart : genomicsCart).some(item => item.cohort === cohort && item.type === header);
                      return (
                        <td key={header} className="p-2 text-center">
                          <div
                            onClick={() => isCollected && (matrixViewType === 'imaging' ? toggleImagingScan(cohort, header) : toggleGenomicsScan(cohort, header))}
                            className={classNames(
                              "w-full h-10 rounded-md flex items-center justify-center transition-all",
                              isCollected
                                ? (isInCart
                                  ? (matrixViewType === 'imaging' ? "bg-pink-600 text-white shadow-md scale-105 border-pink-400" : "bg-indigo-600 text-white shadow-md scale-105 border-indigo-400")
                                  : "bg-emerald-50 text-emerald-600 border border-emerald-100 cursor-pointer hover:bg-emerald-100 hover:scale-105 active:scale-95")
                                : "bg-slate-50 text-slate-300 border border-slate-100/50"
                            )}
                          >
                            {isInCart ? <Check className="w-5 h-5" /> : (isCollected ? <Check className="w-5 h-5" /> : <X className="w-4 h-4 opacity-40" />)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {matrixViewType === 'phenotypic' ? (
            <div className="mt-6 flex items-center gap-4 text-xs text-slate-500 bg-white p-4 rounded-lg border border-slate-200 inline-flex">
              <Info className="w-4 h-4 text-purple-500" />
              <span className="font-medium text-slate-700">Heatmap Distribution (Logarithmic Scale):</span>
              <div className="flex items-center gap-1"><div className="w-6 h-4 bg-slate-50 rounded border border-slate-200"></div> <span>0</span></div>
              <div className="flex items-center gap-1"><div className="w-6 h-4 bg-purple-100 rounded"></div> <span>Low</span></div>
              <div className="flex items-center gap-1"><div className="w-6 h-4 bg-purple-300 rounded"></div></div>
              <div className="flex items-center gap-1"><div className="w-6 h-4 bg-purple-500 rounded"></div></div>
              <div className="flex items-center gap-1"><div className="w-6 h-4 bg-purple-700 rounded"></div></div>
              <div className="flex items-center gap-1"><div className="w-6 h-4 bg-purple-900 rounded"></div> <span>High ({maxVal})</span></div>
            </div>
          ) : (
            <div className="mt-6 flex items-center gap-6 text-xs text-slate-500 bg-white p-4 rounded-lg border border-slate-200 inline-flex">
              <div className="flex items-center gap-2">
                <div className="w-6 h-4 bg-emerald-50 border border-emerald-100 rounded flex items-center justify-center text-emerald-600"><Check className="w-3 h-3" /></div>
                <span className="font-medium text-slate-700">Data Collected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-4 bg-slate-50 border border-slate-100 rounded flex items-center justify-center text-slate-300"><X className="w-2.5 h-2.5" /></div>
                <span className="font-medium text-slate-700">Not Collected</span>
              </div>
            </div>
          )}

          {/* Global AI Semantic Search */}
          <div className="mt-8 bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue rounded-2xl p-6 shadow-md text-white max-w-5xl">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-purple-300" /><h2 className="text-xl font-semibold">Global Semantic Search</h2></div>
            <p className="text-purple-200 text-sm mb-5">Search semantically across all variables in all cohorts. Powered by local AI embeddings.</p>
            <div className="relative flex items-center">
              <input type="text" placeholder="E.g., variables related to sleep patterns or smoking..." value={globalSearchQuery} onChange={(e) => setGlobalSearchQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleGlobalSemanticSearch(); }} disabled={isGlobalSearching} className="w-full bg-white/10 border border-white/20 text-white placeholder-white/60 rounded-xl pl-4 pr-32 py-3 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm transition-all shadow-inner disabled:opacity-50" />
              <div className="absolute right-2 flex items-center gap-1">
                {globalSearchQuery && <button onClick={() => { setGlobalSearchQuery(''); setGlobalSearchResults(null); setGlobalSearchError(null); }} className="p-2 text-white/70 hover:text-white transition-colors"><X className="w-4 h-4" /></button>}
                <button onClick={handleGlobalSemanticSearch} disabled={isGlobalSearching || !data?.length} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 px-3">
                  {isGlobalSearching ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Search className="w-4 h-4 text-white" />}
                  <span className="text-sm font-medium">{isGlobalSearching ? 'Searching...' : 'Search'}</span>
                </button>
              </div>
            </div>
            {semanticModelLoading && <p className="text-purple-300 text-xs mt-3 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Downloading and initialising local embedding model (first time only)...</p>}
            {globalSearchError && <p className="text-red-200 text-sm mt-3 bg-red-900/30 p-2 rounded border border-red-500/30">{globalSearchError}</p>}

            {globalSearchResults !== null && !isGlobalSearching && !globalSearchError && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2"><Check className="w-5 h-5 text-green-400" /> Top Matches across {allCohorts.length} cohorts</h3>
                  <button onClick={() => { setGlobalSearchQuery(''); setGlobalSearchResults(null); setGlobalSearchError(null); }} className="text-xs font-medium bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-md transition-colors">Clear Results</button>
                </div>
                <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-2 pb-2">
                  {globalSearchResults.map(variable => {
                    const isSelected = cart.includes(variable.id);
                    return (
                      <div key={variable.id} className="bg-white/10 border border-white/20 rounded-xl p-4 hover:bg-white/15 transition-all flex flex-col justify-between group">
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded truncate max-w-[120px]" title={variable.Cohort}>{variable.Cohort}</span>
                            <span className="text-[10px] font-semibold opacity-70">{(variable.similarityScore * 100).toFixed(1)}% Match</span>
                          </div>
                          <h4 className="font-mono text-xs font-bold mb-1 truncate text-purple-100" title={variable['Variable Name']}>{variable['Variable Name']}</h4>
                          <p className="text-sm opacity-90 line-clamp-2" title={variable['Variable Label']}>{variable['Variable Label']}</p>
                          <div className="text-[10px] opacity-60 mt-2 uppercase">{variable.Category}</div>
                        </div>
                        <button onClick={() => toggleCartItem(variable.id)} className={classNames("mt-4 w-full py-2 rounded-lg text-xs font-bold transition-all flex justify-center items-center gap-2", isSelected ? "bg-white text-purple-900 border border-transparent shadow shadow-white/20" : "bg-transparent border border-white/30 text-white hover:bg-white/20")}>
                          {isSelected ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                          {isSelected ? 'Added to Cart' : 'Add to Cart'}
                        </button>
                      </div>
                    );
                  })}
                  {globalSearchResults.length === 0 && <div className="col-span-full text-center py-8 text-white/70">No matches found with semantic relevance &gt; 30%</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div >
    );
  };

  const renderGlobalSearchView = () => {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
        {/* Header */}
        <header className="bg-white/90 border-b px-8 py-6 flex flex-col md:flex-row items-center justify-between sticky top-0 z-20 shadow-sm backdrop-blur-xl">
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-brand-pink" />
              Global Semantic Search
            </h1>
            <p className="text-base text-slate-500 mt-1">
              Instantly search across all <span className="font-bold text-brand-purple">{allCohorts.length}</span> cohorts using AI-powered embeddings.
            </p>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center w-full px-4 md:px-8 py-10">
          <section className="w-full mb-10">
            <div className="bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue rounded-3xl p-8 shadow-xl text-white w-full">
              <h2 className="text-2xl font-semibold mb-4 text-white flex items-center gap-3">
                <Search className="w-7 h-7 text-white/80" /> Describe what you are looking for
              </h2>
              <div className="relative flex items-center mb-6">
                <input
                  type="text"
                  placeholder="E.g., variables related to sleep patterns, smoking habits, cognitive decline..."
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleGlobalSemanticSearch(); }}
                  disabled={isGlobalSearching}
                  className="w-full bg-white/10 border-2 border-white/20 hover:border-white/40 text-white placeholder-white/60 rounded-2xl pl-6 pr-36 py-5 text-lg focus:outline-none focus:ring-4 focus:ring-white/80 backdrop-blur-md transition-all disabled:opacity-50"
                />
                <div className="absolute right-3 flex items-center gap-2">
                  {globalSearchQuery && (
                    <button
                      onClick={() => { setGlobalSearchQuery(''); setGlobalSearchResults(null); setGlobalSearchError(null); }}
                      className="p-2.5 text-white/60 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                      title="Clear search"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={handleGlobalSemanticSearch}
                    disabled={isGlobalSearching || !data?.length}
                    className="bg-white text-brand-purple hover:bg-purple-50 active:bg-purple-100 disabled:bg-slate-100 px-6 py-3 rounded-xl font-bold shadow-lg shadow-purple-100/50 transition-all flex items-center gap-2 border border-purple-200"
                  >
                    {isGlobalSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                    <span>{isGlobalSearching ? 'Searching...' : 'Search'}</span>
                  </button>
                </div>
              </div>

              {semanticModelLoading && (
                <div className="mx-auto bg-purple-900/50 border border-purple-500/30 rounded-xl p-4 flex items-center justify-center gap-3 w-fit text-purple-200">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                  <span>Downloading and initialising offline semantic engine (first time only)...</span>
                </div>
              )}

              {globalSearchError && (
                <div className="bg-red-500/20 border-2 border-red-500 rounded-xl p-4 text-red-100 flex items-start gap-3 mt-4">
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" />
                  <p>{globalSearchError}</p>
                </div>
              )}

              {!globalSearchResults && !isGlobalSearching && !semanticModelLoading && !globalSearchError && (
                <div className="mt-12 text-center text-purple-200/60 pb-8">
                  <Database className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Waiting for your query...</p>
                  <p className="text-sm mt-2 opacity-70">Compare over 65,000 variables directly in your browser.</p>
                </div>
              )}
            </div>
          </section>

          {/* Results Section */}
          {globalSearchResults !== null && !isGlobalSearching && !globalSearchError && (
            <section className="w-full">
              <div className="bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-xl border border-slate-200 w-full">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-6">
                  <div>
                    <h3 className="text-2xl font-extrabold flex items-center gap-2 text-brand-purple">
                      <Check className="w-7 h-7 text-green-500" /> Top Results Found
                    </h3>
                    <p className="text-slate-500 text-base mt-1">Showing variables with semantic relevance &gt; 30%</p>
                  </div>
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    {globalSearchResults.length > 0 && (
                      <button
                        onClick={() => {
                          const newVars = globalSearchResults.filter(v => !cart.includes(v.id));
                          if (newVars.length > 0) {
                            toggleGroupCart(newVars);
                          }
                        }}
                        className="bg-brand-purple/10 hover:bg-brand-purple/20 text-brand-purple px-5 py-2 rounded-lg text-base font-semibold border border-brand-purple/20 transition-all flex items-center gap-2"
                      >
                        <ShoppingCart className="w-5 h-5" /> Add All to Cart
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {globalSearchResults.map(variable => {
                    const isSelected = cart.includes(variable.id);
                    return (
                      <div key={variable.id} className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl p-6 hover:border-brand-purple transition-all flex flex-col justify-between group shadow-md">
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-xs font-semibold uppercase tracking-wide bg-brand-purple/10 text-brand-purple border border-brand-purple/20 px-3 py-1 rounded-full truncate max-w-[140px]" title={variable.Cohort}>
                              {variable.Cohort}
                            </span>
                            <span className={classNames("text-xs font-bold px-2 py-1 rounded-full", variable.similarityScore > 0.6 ? "bg-green-100 text-green-700" : variable.similarityScore > 0.4 ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-500")}> 
                              {(variable.similarityScore * 100).toFixed(1)}%
                            </span>
                          </div>
                          <h4 className="font-mono text-lg font-bold mb-1 truncate text-slate-800" title={variable['Variable Name']}>
                            {variable['Variable Name']}
                          </h4>
                          <p className="text-base text-slate-600 line-clamp-3 mb-3 leading-relaxed" title={variable['Variable Label']}>
                            {variable['Variable Label'] || "No label provided"}
                          </p>
                          <div className="flex items-center gap-2 mt-auto">
                            <Layers className="w-4 h-4 text-slate-400" />
                            <div className="text-xs text-slate-500 uppercase tracking-wider">{variable.Category}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleCartItem(variable.id)}
                          className={classNames("mt-5 w-full py-2.5 rounded-lg text-base font-bold transition-all flex justify-center items-center gap-2 border", isSelected ? "bg-brand-purple text-white border-brand-purple shadow-lg shadow-brand-purple/30" : "bg-white border-slate-300 text-brand-purple hover:bg-brand-purple/10 hover:border-brand-purple")}
                        >
                          {isSelected ? <Check className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
                          {isSelected ? 'In Cart' : 'Add to Cart'}
                        </button>
                      </div>
                    );
                  })}
                  {globalSearchResults.length === 0 && (
                    <div className="col-span-full text-center py-12 bg-slate-100 rounded-xl border border-dashed border-brand-purple/20">
                      <Search className="w-12 h-12 mx-auto mb-3 text-brand-purple/30" />
                      <h3 className="text-lg font-medium text-brand-purple/70">No Highly Relevant Matches</h3>
                      <p className="text-sm text-brand-purple/50 mt-1">Try rewording your query with different keywords.</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    );
  };

  const renderDirectoryView = () => {
    let filteredDirData = directoryData;
    if (searchQuery.trim()) {
      filteredDirData = {};
      const lowerQuery = searchQuery.toLowerCase();
      Object.keys(directoryData).forEach(cohortName => {
        const cohortInfo = directoryData[cohortName];
        let hasMatches = false;
        const filteredCategories = {};
        let totalCohortMatches = 0;

        Object.keys(cohortInfo.categories).forEach(catName => {
          const matchedVars = cohortInfo.categories[catName].filter(v =>
            v['Variable Name'].toLowerCase().includes(lowerQuery) ||
            (v['Variable Label'] && v['Variable Label'].toLowerCase().includes(lowerQuery))
          );
          if (matchedVars.length > 0) {
            filteredCategories[catName] = matchedVars;
            totalCohortMatches += matchedVars.length;
            hasMatches = true;
          }
        });

        if (hasMatches) {
          filteredDirData[cohortName] = {
            total: totalCohortMatches,
            categories: filteredCategories
          };
        }
      });
    }

    const activeCohortInfo = activeDirCategory ? filteredDirData[activeDirCategory.cohort] : null;
    const activeVariables = activeCohortInfo?.categories[activeDirCategory.category] || [];
    const catKey = activeDirCategory ? `${activeDirCategory.cohort}___${activeDirCategory.category}` : null;
    const PAGE_SIZE = 50;
    const currentPage = dirCategoryPages[catKey] || 1;
    const totalPages = Math.ceil(activeVariables.length / PAGE_SIZE);
    const paginatedVariables = activeVariables.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const allInCart = activeVariables.length > 0 && activeVariables.every(v => cart.includes(v.id));

    return (
      <div className="flex flex-col h-full bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between sticky top-0 z-10 shadow-sm shrink-0">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <Database className="w-7 h-7 text-purple-600" /> Cohort Data Directory
            </h1>
            <p className="text-sm text-slate-500 mt-2 max-w-3xl">Browse and search variables by navigating through cohorts and their associated categories.</p>
          </div>
          <div className="flex items-center">
            <div className="relative group">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-purple-500 group-focus-within:text-purple-600 transition-colors" />
              <input
                type="text"
                placeholder="Search specific variables (e.g., age, BMI)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-12 py-3 border-2 rounded-xl text-base focus:outline-none focus:ring-4 focus:ring-purple-500/10 w-[450px] transition-all border-purple-500 bg-white text-slate-900 placeholder-slate-400 hover:border-purple-600 focus:border-purple-700 shadow-sm hover:shadow-md"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-600 transition-colors bg-purple-100 hover:bg-purple-200 rounded-full p-0.5"
                  title="Clear Search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* SIDEBAR: Master List */}
          <div className="w-80 border-r border-slate-200 bg-white overflow-y-auto shrink-0 flex flex-col">
            {Object.keys(filteredDirData).length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-sm text-slate-500">No matching folders.</p>
              </div>
            ) : Object.keys(filteredDirData).sort().map(cohortName => {
              const cohortInfo = filteredDirData[cohortName];
              const isCohortExpanded = searchQuery.trim() ? true : expandedDirCohorts[cohortName];

              return (
                <div key={cohortName} className="border-b border-slate-100 last:border-0">
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleDirCohort(cohortName)}
                  >
                    <div className="flex items-center gap-2">
                      {isCohortExpanded ? <ChevronDown className="w-4 h-4 text-purple-600 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                      <Database className={classNames("w-4 h-4 shrink-0", isCohortExpanded ? "text-purple-600" : "text-slate-400")} />
                      <span className="text-sm font-semibold text-slate-800 truncate" title={cohortName}>{cohortName}</span>
                    </div>
                  </div>

                  {isCohortExpanded && (
                    <div className="bg-slate-50 py-1">
                      {Object.keys(cohortInfo.categories).sort().map(categoryName => {
                        const variables = cohortInfo.categories[categoryName];
                        const isActive = activeDirCategory?.cohort === cohortName && activeDirCategory?.category === categoryName;

                        // Calculate cart coverage for this specific category
                        const inCartCount = variables.filter(v => cart.includes(v.id)).length;

                        return (
                          <div
                            key={categoryName}
                            className={classNames(
                              "pl-10 pr-4 py-2 flex items-center justify-between cursor-pointer text-sm transition-colors border-l-2",
                              isActive ? "bg-purple-100/50 border-purple-600 text-purple-900 font-medium" : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                            )}
                            onClick={() => toggleDirCategory(cohortName, categoryName)}
                          >
                            <div className="flex items-center gap-2 overflow-hidden">
                              <Layers className={classNames("w-3.5 h-3.5 shrink-0", isActive ? "text-purple-600" : "text-slate-400")} />
                              <span className="truncate" title={categoryName}>{categoryName}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              {inCartCount > 0 && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold">{inCartCount}/{variables.length}</span>}
                              <span className={classNames("text-xs", isActive ? "text-purple-600 font-bold" : "text-slate-400")}>{variables.length}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* MAIN PANEL: Detail View */}
          <div className="flex-1 bg-slate-50 relative p-6 max-h-full">
            {!activeDirCategory ? (
              <div className="h-full flex flex-col items-center pt-16 px-8 text-slate-500">
                <Layers className="w-16 h-16 text-slate-200 mb-4" />
                <h2 className="text-xl font-medium text-slate-600">Select a Category</h2>
                <p className="text-sm text-slate-400 mt-2 max-w-sm text-center">Choose a cohort category from the left sidebar to view and add variables to your cart.</p>
              </div>
            ) : activeVariables.length === 0 ? (
              <div className="p-8 text-center text-slate-500 h-full flex items-center justify-center">No variables found in this category matching your search.</div>
            ) : (
              <div className="h-full max-w-7xl mx-auto flex flex-col">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
                  <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-20">
                    <div>
                      <div className="flex items-center gap-2 text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">
                        <Database className="w-3 h-3" /> {activeDirCategory.cohort}
                      </div>
                      <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Layers className="w-5 h-5 text-slate-400" /> {activeDirCategory.category}
                      </h2>
                    </div>
                    <button
                      onClick={() => toggleGroupCart(activeVariables)}
                      className={classNames("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm", allInCart ? "bg-purple-100 text-purple-700 hover:bg-purple-200" : "bg-purple-600 text-white hover:bg-purple-700 hover:shadow")}
                    >
                      {allInCart ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                      {allInCart ? 'All Added' : 'Add All to Cart'}
                    </button>
                  </div>

                  <div className="overflow-y-auto overflow-x-auto flex-1 custom-scrollbar">
                    <table className="w-full text-left text-sm relative">
                      <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 sticky top-0 z-10 shadow-sm">
                        <tr>
                          <th className="px-6 py-3 font-semibold w-12 bg-slate-50 border-b border-slate-200"></th>
                          <th className="px-6 py-3 font-semibold whitespace-nowrap bg-slate-50 border-b border-slate-200">Variable Name</th>
                          <th className="px-6 py-3 font-semibold bg-slate-50 border-b border-slate-200">Label</th>
                          <th className="px-6 py-3 font-semibold whitespace-nowrap bg-slate-50 border-b border-slate-200">Table Name</th>
                          <th className="px-6 py-3 font-semibold whitespace-nowrap bg-slate-50 border-b border-slate-200">Data Type</th>
                          <th className="px-6 py-3 font-semibold whitespace-nowrap bg-slate-50 border-b border-slate-200">Completeness</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {paginatedVariables.map(variable => {
                          const isSelected = cart.includes(variable.id);
                          const compData = parseCompleteness(variable.Completeness);
                          return (
                            <tr
                              key={variable.id}
                              className={classNames("hover:bg-purple-50/60 transition-colors cursor-pointer group", isSelected ? "bg-purple-50/40" : "")}
                              onClick={() => toggleCartItem(variable.id)}
                            >
                              <td className="px-6 py-3">
                                <div className={classNames("w-5 h-5 rounded border flex items-center justify-center transition-all group-hover:border-purple-400 text-white", isSelected ? "bg-purple-600 border-purple-600" : "border-slate-300 bg-white")}>
                                  {isSelected && <Check className="w-3.5 h-3.5" />}
                                </div>
                              </td>
                              <td className="px-6 py-3 font-semibold text-slate-900 font-mono text-xs">{variable['Variable Name']}</td>
                              <td className="px-6 py-3 text-slate-600 text-sm leading-relaxed">{variable['Variable Label']}</td>
                              <td className="px-6 py-3 text-slate-500 font-mono text-[11px] whitespace-nowrap">{variable['Filename']}</td>
                              <td className="px-6 py-3">
                                <span className="px-2.5 py-1 rounded bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-medium tracking-wide whitespace-nowrap">{variable['Data Type']}</span>
                              </td>
                              <td className="px-6 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                                    <div className={classNames("h-full transition-all duration-300", compData.color)} style={{ width: `${compData.percent}%` }} />
                                  </div>
                                  <span className="text-[11px] font-medium text-slate-500 w-10 whitespace-nowrap">{compData.text}</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-sm shrink-0 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                      <span className="text-slate-500 font-medium">
                        Showing <span className="text-slate-900">{((currentPage - 1) * PAGE_SIZE) + 1}</span> to <span className="text-slate-900">{Math.min(currentPage * PAGE_SIZE, activeVariables.length)}</span> of <span className="text-slate-900">{activeVariables.length}</span> variables
                      </span>
                      <div className="flex gap-2">
                        <button
                          disabled={currentPage === 1}
                          onClick={() => setDirCategoryPages(prev => ({ ...prev, [catKey]: currentPage - 1 }))}
                          className="px-4 py-2 bg-white border border-slate-200 shadow-sm rounded-lg text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 hover:border-slate-300 transition-all font-semibold active:bg-slate-100"
                        >
                          Previous
                        </button>
                        <button
                          disabled={currentPage === totalPages}
                          onClick={() => setDirCategoryPages(prev => ({ ...prev, [catKey]: currentPage + 1 }))}
                          className="px-4 py-2 bg-white border border-slate-200 shadow-sm rounded-lg text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 hover:border-slate-300 transition-all font-semibold active:bg-slate-100"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOverlapView = () => {
    return (
      <div className="flex flex-col h-full bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm z-10 shrink-0">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <ArrowRightLeft className="w-7 h-7 text-purple-600" /> Cohort Comparison Tool
              </h1>
              <p className="text-slate-600 max-w-3xl mt-2">Compare two cohorts to discover variables with high semantic similarity, helping you find harmonised data across studies.</p>
            </div>
          </div>

          <div className="flex items-end gap-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="flex-1">
              <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Base Cohort (A)</label>
              <select value={overlapCohortA} onChange={(e) => setOverlapCohortA(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none hover:border-purple-300 transition-colors">
                <option value="">Select Cohort...</option>
                {allCohorts.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="flex items-center justify-center -mb-2 mt-4 px-2">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 border-4 border-slate-50 shrink-0">
                <ArrowRightLeft className="w-5 h-5" />
              </div>
            </div>

            <div className="flex-1">
              <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Target Cohort (B)</label>
              <select value={overlapCohortB} onChange={(e) => setOverlapCohortB(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none hover:border-purple-300 transition-colors">
                <option value="">Select Cohort...</option>
                {allCohorts.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="w-48">
              <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Similarity Threshold</label>
              <div className="flex items-center gap-3">
                <input type="range" min="0.5" max="0.95" step="0.05" value={overlapThreshold} onChange={(e) => setOverlapThreshold(parseFloat(e.target.value))} className="w-full accent-purple-600 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer" />
                <span className="text-sm font-bold text-slate-700 w-10">{Math.round(overlapThreshold * 100)}%</span>
              </div>
            </div>

            <button
              onClick={handleCalculateOverlap}
              disabled={!overlapCohortA || !overlapCohortB || overlapCohortA === overlapCohortB || isOverlapCalculating}
              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white rounded-lg font-bold text-sm shadow-md transition-colors flex items-center gap-2 mb-[1px]"
            >
              {isOverlapCalculating ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating</> : <><Sparkles className="w-4 h-4" /> Compare</>}
            </button>
          </div>
          {overlapError && <p className="text-red-500 text-sm mt-3 font-medium bg-red-50 px-4 py-2 rounded-lg border border-red-100">{overlapError}</p>}
        </div>

        <div className="flex-1 overflow-auto p-8">
          {isOverlapCalculating ? (
            <div className="h-full flex flex-col items-center justify-center">
              <div className="w-20 h-20 relative mb-8">
                <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-purple-500 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <h2 className="text-2xl font-bold text-slate-700 mb-2">Analysing Semantic Vector Database...</h2>
              <p className="text-slate-500 text-lg">Comparing every variable across both cohorts using local AI</p>
            </div>
          ) : !overlapResults ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400">
              <div className="relative mb-6">
                <div className="w-32 h-32 rounded-full border-4 border-slate-200 absolute -left-12 mix-blend-multiply bg-indigo-50/50"></div>
                <div className="w-32 h-32 rounded-full border-4 border-slate-200 absolute -right-12 mix-blend-multiply bg-pink-50/50"></div>
                <div className="w-32 h-32 flex items-center justify-center relative z-10"><ArrowRightLeft className="w-12 h-12 text-slate-300" /></div>
              </div>
              <h2 className="text-xl font-medium text-slate-600 mt-4">Select cohorts to visualise comparison</h2>
              <p className="text-sm max-w-sm text-center mt-2">The AI will find similar variables between two different datasets.</p>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto flex flex-col gap-8 h-full">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 shrink-0">
                <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-6 text-center shadow-sm">
                  <h3 className="text-sm font-bold text-indigo-400 uppercase tracking-wider mb-2">Base Variables</h3>
                  <p className="text-4xl font-extrabold text-indigo-900">{overlapResults.totalA}</p>
                  <p className="text-sm text-indigo-600 font-medium mt-1">{overlapCohortA}</p>
                </div>

                <div className="bg-purple-600 border border-purple-700 rounded-xl p-6 text-center text-white shadow-md relative overflow-hidden">
                  <h3 className="text-sm border-b border-purple-500/50 pb-2 mb-3 font-bold uppercase tracking-wider flex items-center justify-center gap-2"><Sparkles className="w-4 h-4 text-purple-300" /> Semantic Comparison</h3>
                  <div className="flex items-center justify-center gap-3">
                    <p className="text-5xl font-extrabold">{overlapResults.pairs.length}</p>
                  </div>
                  <p className="text-sm text-purple-200 mt-2 font-medium">Matched Variables &gt; {Math.round(overlapThreshold * 100)}% Similarity</p>
                </div>

                <div className="bg-pink-50/50 border border-pink-100 rounded-xl p-6 text-center shadow-sm">
                  <h3 className="text-sm font-bold text-pink-400 uppercase tracking-wider mb-2">Target Variables</h3>
                  <p className="text-4xl font-extrabold text-pink-900">{overlapResults.totalB}</p>
                  <p className="text-sm text-pink-600 font-medium mt-1">{overlapCohortB}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 flex flex-col overflow-hidden min-h-0">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between z-10 shrink-0">
                  <h3 className="font-bold text-slate-800">Matched Pairs</h3>
                  {overlapResults.pairs.length > 0 && (
                    <button
                      onClick={() => {
                        const ids = overlapResults.pairs.flatMap(p => [p.varA.id, p.varB.id]).filter(id => !cart.includes(id));
                        if (ids.length > 0) setCart(prev => [...prev, ...ids]);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-bold transition-colors"
                    >
                      <ShoppingCart className="w-4 h-4" /> Add All {overlapResults.pairs.length * 2} to Cart
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
                  {overlapResults.pairs.length === 0 ? (
                    <p className="text-center text-slate-500 py-12">No high-confidence matches found between these specific cohorts at this threshold.</p>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {overlapResults.pairs.map((pair, idx) => {
                        const inCartA = cart.includes(pair.varA.id);
                        const inCartB = cart.includes(pair.varB.id);
                        const isFullMatch = inCartA && inCartB;

                        return (
                          <div key={idx} className={classNames("bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow relative", isFullMatch ? "border-purple-300" : "border-slate-200")}>
                            {isFullMatch && <div className="absolute top-0 right-0 left-0 h-1 bg-purple-500"></div>}
                            <div className="flex flex-col lg:flex-row items-stretch">

                              <div className="flex-1 p-5 border-b lg:border-b-0 lg:border-r border-slate-100 relative group min-w-0">
                                <div className="flex flex-col mb-4">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-mono text-sm font-bold text-slate-900 uppercase tracking-tight">{pair.varA['Variable Name']}</span>
                                    <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[9px] font-bold uppercase tracking-wider">{pair.varA.Category}</span>
                                  </div>
                                  <p className="text-sm text-slate-900 font-medium truncate" title={pair.varA['Variable Label']}>{pair.varA['Variable Label']}</p>
                                </div>
                                <button onClick={() => toggleCartItem(pair.varA.id)} className={classNames("w-full py-2 rounded font-semibold text-xs transition-colors flex items-center justify-center gap-2", inCartA ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100" : "bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 hover:border-indigo-200")}>
                                  {inCartA ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />} {inCartA ? 'Added' : 'Add to Cart'}
                                </button>
                              </div>

                              <div className="shrink-0 flex items-center justify-center bg-slate-50 px-4 py-3 relative border-b lg:border-b-0 lg:border-r lg:-ml-[1px] border-slate-100 z-10 w-full lg:w-32">
                                <div className={classNames("absolute inset-0 opacity-10 pointer-events-none", pair.score > 0.9 ? "bg-emerald-500" : pair.score > 0.8 ? "bg-teal-500" : "bg-purple-500")}></div>
                                <div className="flex flex-col items-center">
                                  <ArrowRightLeft className={classNames("w-5 h-5 mb-1", pair.score > 0.9 ? "text-emerald-600" : pair.score > 0.8 ? "text-teal-600" : "text-purple-600")} />
                                  <span className={classNames("text-lg font-black", pair.score > 0.9 ? "text-emerald-700" : pair.score > 0.8 ? "text-teal-700" : "text-purple-700")}>{Math.round(pair.score * 100)}%</span>
                                  <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400 hidden lg:block">Match</span>
                                </div>
                              </div>

                              <div className="flex-1 p-5 relative group min-w-0 bg-slate-50/50 hover:bg-white transition-colors">
                                <div className="flex flex-col mb-4">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-mono text-sm font-bold text-slate-900 uppercase tracking-tight">{pair.varB['Variable Name']}</span>
                                    <span className="px-1.5 py-0.5 bg-pink-50 text-pink-700 rounded text-[9px] font-bold uppercase tracking-wider">{pair.varB.Category}</span>
                                  </div>
                                  <p className="text-sm text-slate-900 font-medium truncate" title={pair.varB['Variable Label']}>{pair.varB['Variable Label']}</p>
                                </div>
                                <button onClick={() => toggleCartItem(pair.varB.id)} className={classNames("w-full py-2 rounded font-semibold text-xs transition-colors flex items-center justify-center gap-2", inCartB ? "bg-pink-50 text-pink-700 hover:bg-pink-100" : "bg-white text-slate-600 hover:bg-pink-50 hover:text-pink-700 border border-slate-200 hover:border-pink-200 shadow-sm")}>
                                  {inCartB ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />} {inCartB ? 'Added' : 'Add to Cart'}
                                </button>
                              </div>

                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNetworkView = () => {
    const handleZoom = (delta) => {
      setNetworkScale(prev => Math.max(0.2, Math.min(5, prev + delta)));
    };

    const handleMouseDown = (e) => {
      // Check if we're clicking on a node (this will be handled by node-specific events too, 
      // but let's keep this for general background panning)
      setIsPanning(true);
      setLastPanPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e) => {
      if (!isPanning) return;
      const dx = e.clientX - lastPanPos.x;
      const dy = e.clientY - lastPanPos.y;
      setNetworkTranslate(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastPanPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setIsPanning(false);
    };

    return (
      <div className="flex flex-col h-[calc(100vh-64px)] bg-slate-900 overflow-hidden relative select-none">
        <div className="absolute top-8 left-8 z-10 pointer-events-none">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Share2 className="w-7 h-7 text-purple-400" /> Cohort Discovery Network
          </h1>
          <p className="text-slate-400 max-w-xl mt-2">Semantic relationship graph between cohorts. Nodes are study datasets, and connections reveal harmonisation paths found by local AI.</p>
        </div>

        <div className="absolute top-8 right-8 z-20 flex flex-col gap-3">
          <div className="bg-slate-800/80 backdrop-blur-md rounded-xl border border-white/10 p-2 flex flex-col gap-1 shadow-2xl">
            <button onClick={() => handleZoom(0.1)} className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors" title="Zoom In"><ZoomIn className="w-5 h-5" /></button>
            <button onClick={() => handleZoom(-0.1)} className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors" title="Zoom Out"><ZoomOut className="w-5 h-5" /></button>
            <button onClick={() => { setNetworkScale(1); setNetworkTranslate({ x: 0, y: 0 }); }} className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors border-t border-white/5" title="Reset View"><Move className="w-5 h-5" /></button>
          </div>
        </div>

        {isNetworkCalculating ? (
          <div className="h-full flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 text-purple-500 animate-spin mb-4" />
            <h2 className="text-xl font-bold text-white">Mapping Semantic Ecosystem...</h2>
            <p className="text-slate-400 mt-2">Performing high-dimensional AI comparisons</p>
          </div>
        ) : (
          <div
            className="flex-1 relative cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={(e) => {
              // If we clicked the background div, deselect
              if (e.target === e.currentTarget) setSelectedNetworkNode(null);
            }}
          >
            <svg width="100%" height="100%" viewBox="0 0 1000 800" className="w-full h-full">
              <g transform={`translate(${networkTranslate.x}, ${networkTranslate.y}) scale(${networkScale})`}>
                {/* Links */}
                {networkData.links.map((link, i) => {
                  const s = networkData.nodePos ? networkData.nodePos[link.source] : networkData.nodes.find(n => n.id === link.source);
                  const t = networkData.nodePos ? networkData.nodePos[link.target] : networkData.nodes.find(n => n.id === link.target);
                  if (!s || !t) return null;

                  const isHighlighted = selectedNetworkNode && (link.source === selectedNetworkNode || link.target === selectedNetworkNode);
                  const opacity = selectedNetworkNode ? (isHighlighted ? 0.8 : 0.05) : 0.25;
                  const color = isHighlighted ? "#f472b6" : "#a855f7";

                  return (
                    <g key={`link-${i}`}>
                      <line
                        x1={s.x} y1={s.y} x2={t.x} y2={t.y}
                        stroke={color}
                        strokeWidth={Math.min(12, 1 + Math.sqrt(link.value))}
                        strokeLinecap="round"
                        style={{
                          opacity,
                          transition: isPanning ? 'none' : 'all 0.3s ease'
                        }}
                      >
                        <title>{link.source} ↔ {link.target}: {link.value} shared variables</title>
                      </line>
                      {isHighlighted && (
                        <g transform={`translate(${(s.x + t.x) / 2}, ${(s.y + t.y) / 2})`}>
                          <rect x="-15" y="-10" width="30" height="20" rx="4" fill="#f472b6" className="shadow-lg" />
                          <text textAnchor="middle" dy="5" fill="white" className="text-[10px] font-black select-none pointer-events-none">
                            {link.value}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}

                {/* Nodes */}
                {networkData.nodes.map((node, i) => {
                  const isSelected = selectedNetworkNode === node.id;
                  const isNeighbor = selectedNetworkNode && networkData.links.some(l =>
                    (l.source === selectedNetworkNode && l.target === node.id) ||
                    (l.target === selectedNetworkNode && l.source === node.id)
                  );

                  const opacity = selectedNetworkNode ? (isSelected || isNeighbor ? 1 : 0.3) : 1;
                  const color = (isSelected || isNeighbor) ? "#f472b6" : "#a855f7";
                  const scale = isSelected ? 1.2 : 1;

                  return (
                    <g
                      key={i}
                      transform={`translate(${node.x},${node.y}) scale(${scale})`}
                      className="group cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedNetworkNode(node.id === selectedNetworkNode ? null : node.id);
                      }}
                      style={{
                        opacity,
                        transition: isPanning ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <circle
                        r={Math.max(15, Math.min(60, 10 + Math.sqrt(node.count)))}
                        fill={isSelected ? "#500733" : "#1e1b4b"}
                        stroke={color}
                        strokeWidth={isSelected ? "5" : "3"}
                        className="transition-all group-hover:r-[110%]"
                      />
                      <text
                        y={Math.max(25, 10 + Math.sqrt(node.count)) + 20}
                        textAnchor="middle"
                        fill="white"
                        className={classNames("text-[12px] pointer-events-none select-none drop-shadow-lg", isSelected ? "font-black" : "font-bold")}
                      >
                        {node.id}
                      </text>
                      <text
                        y={Math.max(25, 10 + Math.sqrt(node.count)) + 35}
                        textAnchor="middle"
                        fill={isSelected || isNeighbor ? "#f9a8d4" : "#94a3b8"}
                        className="text-[10px] font-medium pointer-events-none select-none"
                      >
                        {node.count} variables
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>

            <div className="absolute bottom-8 right-8 bg-black/50 backdrop-blur-md p-4 rounded-xl border border-white/10 text-[10px] text-slate-300 pointer-events-none shadow-2xl">
              <p className="font-bold text-white mb-2 uppercase tracking-wider">Visualization Key</p>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 rounded-full border-2 border-pink-500 bg-pink-950/40"></div>
                <span>Selected / Connected Cohort</span>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 rounded-full border-2 border-purple-500 bg-indigo-950"></div>
                <span>Other Dataset</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-1 bg-pink-500/80 rounded"></div>
                <span>Shared Variable Count (Highlighted)</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const GhostTypingSearch = ({ onSearch }) => {
    const phrases = [
      "cognitive decline and MMSE scores",
      "hippocampal volume in MRI scans",
      "APOE4 genetic carrier status",
      "lifestyle factors and education level",
      "diet and exercise"
    ];
    const [currentPhraseIdx, setCurrentPhraseIdx] = useState(0);
    const [currentText, setCurrentText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [typingSpeed, setTypingSpeed] = useState(100);
    const [userInput, setUserInput] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (isFocused || userInput) return;
      const handleTyping = () => {
        const fullPhrase = phrases[currentPhraseIdx];

        if (isDeleting) {
          setCurrentText(fullPhrase.substring(0, currentText.length - 1));
          setTypingSpeed(50);
        } else {
          setCurrentText(fullPhrase.substring(0, currentText.length + 1));
          setTypingSpeed(100);
        }

        if (!isDeleting && currentText === fullPhrase) {
          setTimeout(() => setIsDeleting(true), 2000);
        } else if (isDeleting && currentText === "") {
          setIsDeleting(false);
          setCurrentPhraseIdx((currentPhraseIdx + 1) % phrases.length);
        }
      };

      const timer = setTimeout(handleTyping, typingSpeed);
      return () => clearTimeout(timer);
    }, [currentText, isDeleting, currentPhraseIdx, isFocused, userInput]);

    return (
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
        <div className={classNames(
          "relative flex items-center bg-white border rounded-xl px-5 py-3 shadow-sm transition-all",
          isFocused ? "border-purple-500 ring-2 ring-purple-100 shadow-lg" : "border-slate-200 group-hover:border-purple-300"
        )}>
          <Sparkles className={classNames("w-4 h-4 mr-3 transition-colors", isFocused || userInput ? "text-purple-600" : "text-slate-400")} />
          <div className="flex-1 flex items-center text-sm font-medium">
            <span className="text-slate-400 whitespace-nowrap mr-2">Find variables relating to</span>
            <div className="relative flex-1 h-5">
              <input
                type="text"
                value={userInput}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSearch(userInput || currentText);
                }}
                className="absolute inset-0 w-full bg-transparent outline-none text-slate-800 font-bold z-10"
              />
              {!userInput && !isFocused && (
                <span className="absolute left-0 top-0 bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue bg-clip-text text-transparent font-bold inline-block min-w-[1px] border-r-2 border-brand-purple">
                  {currentText}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => onSearch(userInput || currentText)}
            className="ml-auto flex items-center gap-2 group/btn"
          >
            <span className={classNames(
              "text-[10px] font-black uppercase tracking-widest transition-colors",
              isFocused || userInput ? "text-purple-600" : "text-slate-300 group-hover:text-purple-400"
            )}>
              {isFocused || userInput ? "Press Enter" : "AI Powered"}
            </span>
            <div className={classNames(
              "w-6 h-6 rounded-lg flex items-center justify-center transition-all",
              isFocused || userInput ? "bg-purple-600 text-white shadow-md" : "bg-slate-50 text-slate-300 group-hover:bg-purple-50 group-hover:text-purple-600"
            )}>
              <Search className="w-3 h-3" />
            </div>
          </button>
        </div>
      </div>
    );
  };

  const renderHomeView = () => (
    <div className="min-h-full bg-slate-50 flex flex-col pt-12 p-8 pb-24">
      <div className="max-w-5xl mx-auto w-full">
        <div className="text-center mb-12">
          <img
            src={process.env.PUBLIC_URL + '/NDP_Logo_New2.png'}
            alt="Logo"
            className="mx-auto mb-6 h-28 w-auto object-contain"
            style={{ maxHeight: '112px' }}
          />
          <h1 className="text-4xl pr-8 pl-8 font-extrabold text-slate-800 tracking-tight sm:text-5xl mb-4">
            Welcome to The <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue">Neurological Data Platform</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-slate-500">
            Explore and discover variables across multiple cohorts within the Neurological Data Platform hubs. Choose a tool below to begin your research data selection.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 mt-8 mb-2">
            <img src={process.env.PUBLIC_URL + '/dpuk.png'} alt="DPUK" className="h-12 w-auto object-contain" style={{maxHeight:'48px'}} />
            <img src={process.env.PUBLIC_URL + '/bw.webp'} alt="BrainWaves" className="h-12 w-auto object-contain" style={{maxHeight:'48px'}} />
            <img src={process.env.PUBLIC_URL + '/mnd.png'} alt="MND" className="h-12 w-auto object-contain" style={{maxHeight:'48px'}} />
            <img src={process.env.PUBLIC_URL + '/tbi.png'} alt="TBI" className="h-12 w-auto object-contain" style={{maxHeight:'48px'}} />
          </div>
        </div>

        <div className="max-w-5xl mx-auto mb-12">
          <GhostTypingSearch onSearch={(val) => {
            setGlobalSearchQuery(val);
            setGlobalSearchResults(null);
            setGlobalSearchError(null);
            setCurrentView('global-search');
          }} />
        </div>


        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
          <div className="bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-all border border-white/10">
            <p className="text-[10px] font-black text-white/90 uppercase tracking-widest mb-1">Total Cohorts</p>
            <p className="text-3xl font-black text-white mb-1"><AnimatedCounter end={60} suffix="+" /></p>
            <p className="text-[11px] text-white/80 font-medium">Cohort datasets</p>
          </div>
          <div className="bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-all border border-white/10">
            <p className="text-[10px] font-black text-white/90 uppercase tracking-widest mb-1">Participants</p>
            <p className="text-3xl font-black text-white mb-1"><AnimatedCounter end={3500000} suffix="+" /></p>
            <p className="text-[11px] text-white/80 font-medium">Unique individuals</p>
          </div>
          <div className="bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-all border border-white/10">
            <p className="text-[10px] font-black text-white/90 uppercase tracking-widest mb-1">Variables</p>
            <p className="text-3xl font-black text-white mb-1"><AnimatedCounter end={65000} suffix="+" /></p>
            <p className="text-[11px] text-white/80 font-medium">To choose from</p>
          </div>
          <div className="bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-all border border-white/10">
            <p className="text-[10px] font-black text-white/90 uppercase tracking-widest mb-1">Imaging Scans</p>
            <p className="text-3xl font-black text-white mb-1"><AnimatedCounter end={150000} suffix="+" /></p>
            <p className="text-[11px] text-white/80 font-medium">MRI / MEG / PET / CT</p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <button
            onClick={() => setCurrentView('matrix')}
            className="group flex flex-col bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-purple-300 transition-all text-left w-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Table className="w-32 h-32" />
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <Table className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">Matrix View</h2>
            <p className="text-slate-500 leading-relaxed text-sm mb-6 flex-1">
              Visualise cohort data availability as a high-level density heatmap. Quickly find out what categories of data are available in each cohort.
            </p>
            <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all gap-1 mt-auto">
              Explore Matrix <ArrowLeft className="w-4 h-4 rotate-180" />
            </div>
          </button>

          <button
            onClick={() => setCurrentView('directory')}
            className="group flex flex-col bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-purple-300 transition-all text-left w-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Database className="w-32 h-32" />
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <Database className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">Directory View</h2>
            <p className="text-slate-500 leading-relaxed text-sm mb-6 flex-1">
              Browse the complete hierarchical tree of variables across all cohorts. Search for specific variables and add entire categories to your cart directly.
            </p>
            <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all gap-1 mt-auto">
              Browse Directory <ArrowLeft className="w-4 h-4 rotate-180" />
            </div>
          </button>

          <button
            onClick={() => setCurrentView('overlap')}
            className="group flex flex-col bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-purple-300 transition-all text-left w-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <ArrowRightLeft className="w-32 h-32" />
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <ArrowRightLeft className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">Compare Cohorts</h2>
            <p className="text-slate-500 leading-relaxed text-sm mb-6 flex-1">
              Select two cohort datasets to automatically map, compare and visualise the similar overlapping variables they both collected.
            </p>
            <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all gap-1 mt-auto">
              Compare Cohorts <ArrowLeft className="w-4 h-4 rotate-180" />
            </div>
          </button>

          <button
            onClick={() => setCurrentView('cohort-library')}
            className="group flex flex-col bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-purple-300 transition-all text-left w-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Package className="w-32 h-32" />
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <Package className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">Cohort Library</h2>
            <p className="text-slate-500 leading-relaxed text-sm mb-6 flex-1">
              Browse the complete list of available cohort datasets. View subject counts and check the availability of phenotypic, imaging, and genomics data.
            </p>
            <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all gap-1 mt-auto">
              Browse Library <ArrowLeft className="w-4 h-4 rotate-180" />
            </div>
          </button>

          <button
            onClick={() => setCurrentView('network')}
            className="group flex flex-col bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-purple-300 transition-all text-left w-full relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
              <Share2 className="w-32 h-32" />
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-6 group-hover:scale-110 transition-transform shadow-sm">
              <Share2 className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">Network Graph</h2>
            <p className="text-slate-500 leading-relaxed text-sm mb-6 flex-1">
              Explore the relationships between variables and cohorts through an interactive graph. Discover which variables are related to each other.
            </p>
            <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all gap-1 mt-auto">
              View Network <ArrowLeft className="w-4 h-4 rotate-180" />
            </div>
          </button>

          <div className="p-[1.5px] bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group">
            <button
              onClick={() => setCurrentView('global-search')}
              className="flex flex-col bg-white rounded-[calc(1rem-1.5px)] p-8 text-left h-full w-full relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <Sparkles className="w-32 h-32 text-white" />
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform shadow-md">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">Global AI Search</h2>
              <p className="text-slate-500 leading-relaxed text-sm mb-6 flex-1">
                Describe the concepts you are researching in natural language, and let local semantic AI find the most relevant variables across our entire database.
              </p>
              <div className="flex items-center text-purple-700 font-semibold text-sm group-hover:gap-2 transition-all gap-1 mt-auto">
                Start Searching <ArrowLeft className="w-4 h-4 rotate-180" />
              </div>
            </button>
          </div>

        </div>

      </div>
    </div>
  );

  const renderDataDiscoveryView = () => {
    const discoveryTools = [
      {
        id: 'cohort-library',
        title: 'Cohort Library',
        icon: Package,
        description: 'Browse and explore our comprehensive collection of cohorts with detailed metadata.'
      },
      {
        id: 'matrix',
        title: 'Matrix View',
        icon: Table,
        description: 'Conduct side-by-side variable comparisons across multiple cohorts in a structured matrix.'
      },
      {
        id: 'directory',
        title: 'Variable Directory',
        icon: Database,
        description: 'Search our exhaustive dictionary of variables to find specific data points you need.'
      },
      {
        id: 'overlap',
        title: 'Compare Cohorts',
        icon: ArrowRightLeft,
        description: 'Analyze participant and sample overlaps between selected cohorts.'
      },
      {
        id: 'network',
        title: 'Network Graph',
        icon: Share2,
        description: 'Visualize the complex web of links between diseases, variables, and distinct cohorts.'
      },
      {
        id: 'global-search',
        title: 'AI Search',
        icon: Sparkles,
        description: 'Use advanced natural language processing to uncover hidden insights and relevant datasets.'
      }
    ];

    return (
      <div className="flex-1 bg-slate-50 relative pb-24 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="bg-white border-b border-slate-200 px-6 py-12 md:py-16 text-center shadow-sm">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-purple-700 font-bold text-xs uppercase tracking-widest mb-6 border border-purple-100">
            <LayoutGrid className="w-4 h-4" /> Tool Suite
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-800 mb-4 tracking-tight">
            Data Discovery Tools
          </h1>
          <p className="text-slate-500 text-lg max-w-3xl mx-auto leading-relaxed">
            A comprehensive suite of powerful features designed to help you explore, compare, and understand our cohorts and variables.
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {discoveryTools.map((tool) => {
              // Match Home page card structure and icon color logic exactly
              let iconBg = "bg-purple-100";
              let iconColor = "text-purple-600";
              let shadow = "shadow-sm";
              if (tool.id === 'matrix') { iconBg = "bg-purple-100"; iconColor = "text-purple-600"; }
              if (tool.id === 'directory') { iconBg = "bg-orange-100"; iconColor = "text-orange-500"; }
              if (tool.id === 'cohort-library') { iconBg = "bg-blue-100"; iconColor = "text-blue-500"; }
              if (tool.id === 'overlap') { iconBg = "bg-fuchsia-100"; iconColor = "text-purple-500"; }
              if (tool.id === 'network') { iconBg = "bg-pink-100"; iconColor = "text-pink-500"; }
              if (tool.id === 'global-search') { iconBg = "bg-gradient-to-br from-brand-pink via-brand-purple to-brand-blue"; iconColor = "text-white"; shadow = "shadow-md"; }
              const Icon = tool.icon;
              return (
                <button
                  key={tool.id}
                  onClick={() => setCurrentView(tool.id)}
                  className={`group flex flex-col bg-white rounded-2xl p-8 border border-slate-200 ${shadow} hover:shadow-xl hover:-translate-y-1 hover:border-purple-300 transition-all text-left w-full relative overflow-hidden`}
                >
                  <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    {tool.id === 'global-search' ? <Sparkles className="w-32 h-32 text-white" /> : <Icon className="w-32 h-32 opacity-100" />}
                  </div>
                  <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${shadow}`}>
                    <Icon className={`w-6 h-6 ${iconColor}`} />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">{tool.title}</h2>
                  <p className="text-slate-500 leading-relaxed text-sm mb-6 flex-1">
                    {tool.description}
                  </p>
                  <div className={`flex items-center text-purple-600 font-semibold text-sm group-hover:gap-2 transition-all gap-1 mt-auto ${tool.id === 'global-search' ? 'text-purple-700' : ''}`}>
                    {tool.id === 'global-search' ? 'Start Searching' : 'Explore Tool'} <ArrowLeft className="w-4 h-4 rotate-180" />
                  </div>
                </button>
              );
            })}
          </div>
          
          <div className="mt-24 flex flex-col items-center max-w-5xl mx-auto border-t border-slate-200 pt-16">
            <h3 className="text-2xl font-bold text-slate-800 mb-12 flex items-center gap-2 tracking-tight">
              Start Building Your Research Selection
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 w-full relative">
              <div className="hidden md:block absolute top-10 left-[16.66%] right-[16.66%] h-[2px] bg-gradient-to-r from-purple-100 via-purple-200 to-pink-100 -z-10"></div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm relative overflow-hidden group">
                  <div className="absolute inset-0 bg-indigo-600 opacity-0 group-hover:opacity-5 transition-opacity"></div>
                  <Search className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" />
                  <span className="absolute -top-2 -right-2 bg-indigo-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-[3px] border-white shadow-sm">1</span>
                </div>
                <h4 className="text-xl font-bold text-slate-800 mb-3">Discover Data</h4>
                <p className="text-slate-500 leading-relaxed max-w-[280px]">
                  Use the tools above to locate the specific variables you need for your research across all available cohorts.
                </p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm relative overflow-hidden group">
                  <div className="absolute inset-0 bg-purple-600 opacity-0 group-hover:opacity-5 transition-opacity"></div>
                  <ShoppingCart className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" />
                  <span className="absolute -top-2 -right-2 bg-purple-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-[3px] border-white shadow-sm">2</span>
                </div>
                <h4 className="text-xl font-bold text-slate-800 mb-3">Build Your Cart</h4>
                <p className="text-slate-500 leading-relaxed max-w-[280px]">
                  Click <span className="font-semibold text-slate-700">"Add to Cart"</span> next to variables or categories to save your selections into your personal Research Cart.
                </p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-pink-50 text-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm relative overflow-hidden group">
                  <div className="absolute inset-0 bg-pink-600 opacity-0 group-hover:opacity-5 transition-opacity"></div>
                  <Download className="w-8 h-8 group-hover:scale-110 transition-transform duration-300" />
                  <span className="absolute -top-2 -right-2 bg-pink-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-[3px] border-white shadow-sm">3</span>
                </div>
                <h4 className="text-xl font-bold text-slate-800 mb-3">Export Selection</h4>
                <p className="text-slate-500 leading-relaxed max-w-[280px]">
                  Open your Cart, review your data, provide project details, and <span className="font-semibold text-slate-700">Export a CSV</span> to submit for formal data access.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDataAccessView = () => (
    <div className="flex-1 overflow-auto bg-slate-50 relative pb-24">
      {/* Clean, integrated header */}
      <div className="bg-white border-b border-slate-200 px-6 py-12 md:py-16 text-center shadow-sm">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-purple-700 font-bold text-xs uppercase tracking-widest mb-6 border border-purple-100">
          <FileText className="w-4 h-4" /> Step-by-Step Guide
        </div>
        <h1 className="text-3xl md:text-5xl font-extrabold text-slate-800 mb-4 tracking-tight">
          Data Access Process
        </h1>
        <p className="text-slate-500 text-lg max-w-3xl mx-auto leading-relaxed">
          The Neurological Data Platform provides a secure, transparent, and structured application process for researchers to request and analyse cohort data.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[
            {
              title: "Who can Access?",
              icon: <User className="w-6 h-6" />,
              content: "The NDP is available to bona fide researchers globally for health-related research in the public interest. A 'bona fide' researcher is affiliated with a recognised research institution and possesses the expertise for the proposed project. Students must apply as co-applicants with a senior researcher/supervisor as the Lead Applicant."
            },
            {
              title: "Data Discovery",
              icon: <Database className="w-6 h-6" />,
              content: "Utilise our Discovery Matrix, Directory, and AI Search tools to identify specific variables across all of our cohorts. Add these to your Research Cart to build your selection."
            },
            {
              title: "Submit Application",
              icon: <ClipboardList className="w-6 h-6" />,
              link: "https://data.dpuk.ukserp.ac.uk/Apply",
              buttonText: "Go to Portal",
              content: "Once your data list is exported from the Research Cart, complete the official online application. You will need to provide a scientific justification, project end date, and list any co-applicants."
            },
            {
              title: "Scientific Triage",
              icon: <Search className="w-6 h-6" />,
              link: "https://portal.dementiasplatform.uk/wp-content/uploads/2025/02/DPUK-Application-guidance-20252602FINAL-v1.pdf",
              buttonText: "View Guidance",
              content: "The application will then be reviewed to ensure that the guidance has been abided by and is a viable application to consider moving forward."
            },
            {
              title: "AI/ML Triage",
              icon: <Brain className="w-6 h-6" />,
              link: "https://portal.dementiasplatform.uk/Reports/AIRiskReport/",
              buttonText: "AI Risk Report",
              content: "Should your application include AI or ML work, we will require additional information. It is advised that you review our report on AI models applied to sensitive healthcare data before submitting."
            },
            {
              title: "Commercial Triage",
              icon: <Briefcase className="w-6 h-6" />,
              content: "We welcome applications from all areas. For those deriving from industry, we require additional information to be submitted, which includes the applicants' CVs."
            },
            {
              title: "Guardian Approval",
              icon: <Shield className="w-6 h-6" />,
              content: "Your proposal is circulated to the data guardians of the requested cohorts. They may ask you to clarify parts of your application before approving or declining. Decisions typically take 28 days."
            },
            {
              title: "Access Agreement",
              icon: <FileText className="w-6 h-6" />,
              link: "https://portal.dementiasplatform.uk/wp-content/uploads/2025/05/UNRESTRICTED_DPUKDataAccessAgreement-V12-pdf.pdf",
              buttonText: "Sample DAA",
              content: "Approved applications require a signed NDP Data Access Agreement. This legal contract must be executed by both the researcher and their host institution before any data access is granted."
            },
            {
              title: "Analysis Environment",
              icon: <Lock className="w-6 h-6" />,
              link: "https://portal.dementiasplatform.uk/analyse-data/analysis-environment/",
              buttonText: "Environment Details",
              content: "You will gain access to cohort data via two-factor authentication. As the analysis platform is housed within a separate remote desktop, data cannot be downloaded locally. Summary tables for reporting may be downloaded following an approval system."
            }
          ].map((step, idx) => (
            <ScrollReveal key={idx} className="h-full">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 h-full flex flex-col hover:shadow-lg hover:border-purple-200 transition-all duration-300 group relative">
                <div className="absolute top-6 right-6 text-5xl font-black text-slate-50 opacity-50 group-hover:text-purple-50 transition-colors pointer-events-none">
                  {idx + 1}
                </div>
                
                <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-purple-600 group-hover:text-white transition-all shadow-sm">
                  {step.icon}
                </div>
                
                <h4 className="text-lg font-bold text-slate-800 mb-3 group-hover:text-purple-700 transition-colors">
                  {step.title}
                </h4>
                
                <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-1 relative z-10">
                  {step.content}
                </p>
                
                {step.link && (
                  <div className="mt-auto pt-4 border-t border-slate-100">
                    <a
                      href={step.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-between w-full p-2.5 rounded-lg bg-slate-50 hover:bg-purple-50 text-slate-700 hover:text-purple-700 text-sm font-semibold transition-colors border border-transparent hover:border-purple-100"
                    >
                      {step.buttonText} <ExternalLink className="w-4 h-4 ml-2 opacity-60" />
                    </a>
                  </div>
                )}
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCohortLibraryView = () => {
    return (
      <div className="flex flex-col h-full bg-slate-50 animate-in fade-in duration-500">
        <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm z-10 shrink-0">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <Package className="w-7 h-7 text-purple-600" /> Cohort Library
              </h1>
              <p className="text-slate-600 max-w-3xl mt-2">
                Browse the cohort datasets that are available to apply for in the NDP.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full flex flex-col xl:flex-row p-6 gap-8 overflow-y-auto">
            {/* Left Sidebar: AI Tool */}
            <div className="w-full xl:w-96 shrink-0 group">
              <div className="bg-gradient-to-br from-indigo-900 via-indigo-950 to-purple-900 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden h-full flex flex-col border border-indigo-500/20">
                <div className="absolute -top-24 -right-24 p-8 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
                  <Sparkles className="w-64 h-64 rotate-12" />
                </div>

                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 border border-white/20">
                    <Sparkles className="w-6 h-6 text-indigo-300" />
                  </div>
                  <h2 className="text-2xl font-black mb-4 leading-tight">Find Suitable Cohorts</h2>
                  <p className="text-indigo-100/80 text-sm leading-relaxed mb-8">
                    Describe your research question and our AI will analyse over 65,000 variables to find the most relevant datasets.
                  </p>

                  <div className="bg-white/5 backdrop-blur-md rounded-2xl p-1.5 border border-white/10 shadow-inner mb-6 transition-all focus-within:bg-white/10 focus-within:border-white/30">
                    <textarea
                      value={researchQuestion}
                      onChange={(e) => setResearchQuestion(e.target.value)}
                      placeholder="e.g. Is there a link between depression and dementia?"
                      className="w-full px-4 py-4 text-sm border-0 rounded-xl focus:ring-0 bg-transparent text-white placeholder-white/30 outline-none resize-none min-h-[120px]"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleResearchSearch();
                        }
                      }}
                    />
                    <div className="flex justify-end p-1.5">
                      <button
                        onClick={handleResearchSearch}
                        disabled={isResearchSearching || !researchQuestion.trim()}
                        className="w-full py-3 bg-white text-indigo-900 font-bold rounded-xl text-xs hover:bg-indigo-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg active:scale-95"
                      >
                        {isResearchSearching ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing...</> : <><Search className="w-3.5 h-3.5" /> Analyse Question</>}
                      </button>
                    </div>
                  </div>

                  {researchSearchError && (
                    <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-100 font-medium text-xs flex items-center gap-3 animate-in shake duration-500">
                      <Info className="w-4 h-4 flex-shrink-0" /> {researchSearchError}
                    </div>
                  )}
                </div>

                <div className="relative z-10 flex-1 flex flex-col min-h-[300px]">
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-300/60 mb-4 flex items-center gap-2">
                    {researchCohortResults ? 'AI Recommendations' : 'Awaiting Input'}
                  </h3>

                  {researchCohortResults ? (
                    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      {researchCohortResults.length === 0 ? (
                        <div className="bg-white/5 border border-dashed border-white/10 rounded-2xl p-6 text-center">
                          <p className="text-xs text-indigo-200 italic">No specific matches. Try broader terms.</p>
                        </div>
                      ) : (
                        researchCohortResults.map((res, idx) => (
                          <div
                            key={res.cohort}
                            className="bg-white/5 backdrop-blur-sm hover:bg-white/10 rounded-2xl p-4 flex justify-between items-center border border-white/5 transition-all cursor-pointer group/item"
                            onClick={() => {
                              setSelectedCohort(res.cohort);
                              setCurrentView('cohort');
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-600/30 border border-indigo-400/20 font-black text-xs text-indigo-200 group-hover/item:bg-indigo-500 transition-colors">
                                #{idx + 1}
                              </div>
                              <div className="flex flex-col">
                                <span className="font-bold text-sm text-white">{res.cohort}</span>
                                <span className="text-[10px] text-indigo-300/80 font-medium">{res.matchedVars} matches</span>
                              </div>
                            </div>
                            <ArrowLeft className="w-4 h-4 text-indigo-400 rotate-180 group-hover/item:translate-x-1 transition-transform" />
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-6 bg-white/5 border border-dashed border-white/10 rounded-3xl opacity-40">
                      <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center mb-4">
                        <Search className="w-6 h-6 text-indigo-300" />
                      </div>
                      <p className="text-xs text-indigo-200 font-medium leading-relaxed"> Results will appear here after analysis.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Cohort Grid */}
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
                {cohortDescriptives.map((cohort, idx) => (
                  <div key={idx} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col group">
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-4">
                        <div className="px-3 py-1 bg-purple-100 rounded-lg text-purple-700 font-black text-xs">
                          {cohort.Cohort}
                        </div>
                        <div className="bg-slate-50 px-3 py-1 rounded-full text-[10px] font-bold text-slate-500 border border-slate-100 uppercase tracking-wider">
                          {cohort.Participants} Subjects
                        </div>
                      </div>

                      <h3 className="text-lg font-bold text-slate-800 mb-2 truncate group-hover:text-purple-700 transition-colors" title={cohort['Full Cohort Name']}>
                        {cohort['Full Cohort Name']}
                      </h3>

                      <p className="text-slate-500 text-[13px] leading-relaxed mb-6 line-clamp-4 flex-1">
                        {cohort.Description}
                      </p>

                      <div className="pt-6 border-t border-slate-50 mt-auto">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Types of Data Available</p>
                        <div className="flex gap-3">
                          {cohort.Phenotypic === 'TRUE' && (
                            <div
                              onClick={() => {
                                setMatrixViewType('phenotypic');
                                setCurrentView('matrix');
                              }}
                              className="group/icon relative w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100 hover:bg-purple-600 hover:text-white transition-all shadow-sm cursor-pointer"
                            >
                              <ClipboardList className="w-5 h-5" />
                              <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">Phenotypic</span>
                            </div>
                          )}
                          {cohort.Imaging === 'TRUE' && (
                            <div
                              onClick={() => {
                                setMatrixViewType('imaging');
                                setCurrentView('matrix');
                              }}
                              className="group/icon relative w-10 h-10 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center border border-pink-100 hover:bg-pink-600 hover:text-white transition-all shadow-sm cursor-pointer"
                            >
                              <Brain className="w-5 h-5" />
                              <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">Imaging</span>
                            </div>
                          )}
                          {cohort.Genomics === 'TRUE' && (
                            <div
                              onClick={() => {
                                setMatrixViewType('genomics');
                                setCurrentView('matrix');
                              }}
                              className="group/icon relative w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all shadow-sm cursor-pointer"
                            >
                              <Dna className="w-5 h-5" />
                              <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">Genomics</span>
                            </div>
                          )}
                          {cohort.Longitudinal === 'TRUE' && (
                            <div className="group/icon relative w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 hover:bg-emerald-600 hover:text-white transition-all shadow-sm">
                              <Clock className="w-5 h-5" />
                              <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">Longitudinal</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedCohort(cohort.Cohort);
                        setCurrentView('cohort');
                      }}
                      className="w-full py-4 bg-slate-50 border-t border-slate-100 text-purple-600 font-bold text-xs uppercase tracking-widest hover:bg-purple-600 hover:text-white transition-all active:scale-[0.98]"
                    >
                      Explore Variable Database
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSyntheticLibraryView = () => {
    const syntheticDatasets = [
      {
        id: 'camcan',
        name: 'Cam-CAN Synthetic Dataset',
        filename: 'camcan-imaging_synthetic_data.csv',
        rows: 5000,
        cols: 90,
        cohort: 'Cam-CAN',
        description: 'Synthetic imaging data based on the Cambridge Centre for Ageing and Neuroscience cohort.',
        type: 'Medium Fidelity'
      },
      {
        id: 'memento',
        name: 'Memento Synthetic Dataset',
        filename: 'memento-imaging_synthetic_data.csv',
        rows: 10000,
        cols: 70,
        cohort: 'Memento',
        description: 'Synthetic longitudinal data derived from the French Memento study on cognitive decline.',
        type: 'High Fidelity'
      },
      {
        id: 'whitehall',
        name: 'Whitehall II Synthetic Dataset',
        filename: 'whitehall-imaging_synthetic_data.csv',
        rows: 5000,
        cols: 90,
        cohort: 'Whitehall II',
        description: 'Synthetic variables representative of the Whitehall II cohort study on social determinants of health.',
        type: 'Medium Fidelity'
      }
    ];

    const handleRequestAccess = (dataset) => {
      setRequestingDataset(dataset);
      setSyntheticAccessModalOpen(true);
    };

    const handleSubmitRequest = async (e) => {
      e.preventDefault();
      setIsSendingEmail(true);
      setEmailSentStatus(null);

      const templateParams = {
        to_email: 'lewishotchkiss123@gmail.com',
        dataset_name: requestingDataset?.name,
        user_name: syntheticForm.fullName,
        user_email: syntheticForm.email,
        institution: syntheticForm.institution,
        job_role: syntheticForm.jobRole,
        planned_use: syntheticForm.plannedUse
      };

      try {
        // You'll need to set these in your .env or replace them here after setting up EmailJS
        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_id_placeholder';
        const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_id_placeholder';
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'public_key_placeholder';

        await emailjs.send(serviceId, templateId, templateParams, publicKey);

        setEmailSentStatus('success');
        setAuthorizedDatasets(prev => [...prev, requestingDataset.filename]);

        // Close modal after a short delay to show success
        setTimeout(() => {
          setSyntheticAccessModalOpen(false);
          setIsSendingEmail(false);
          setEmailSentStatus(null);
          setSyntheticForm({
            fullName: '',
            email: '',
            institution: '',
            jobRole: '',
            plannedUse: ''
          });
        }, 1500);
      } catch (error) {
        console.error('Failed to send email:', error);
        setEmailSentStatus('error');
        setIsSendingEmail(false);
      }
    };

    return (
      <div className="flex flex-col h-full bg-slate-50 animate-in fade-in duration-500">
        <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm z-10 shrink-0">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <BarChart3 className="w-7 h-7 text-purple-600" /> Synthetic Data Library
              </h1>
              <p className="text-slate-600 max-w-3xl mt-2">
                Download synthetic datasets for testing your analysis pipelines, assessing project feasibility or training & teaching purposes.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {syntheticDatasets.map(dataset => {
              const isAuthorized = authorizedDatasets.includes(dataset.filename);
              return (
                <div key={dataset.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col group">
                  <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-6 text-white relative">
                    <BarChart3 className="absolute top-4 right-4 w-12 h-12 opacity-20 group-hover:scale-110 transition-transform" />
                    <div className="bg-white/20 backdrop-blur-md rounded-lg px-3 py-1 text-[10px] font-bold uppercase tracking-wider inline-block mb-3 border border-white/30">
                      {dataset.type} Dataset
                    </div>
                    <h3 className="text-xl font-bold mb-1">{dataset.name}</h3>
                    <p className="text-purple-100 text-sm font-medium">{dataset.cohort}</p>
                  </div>

                  <div className="p-6 flex-1 flex flex-col">
                    <p className="text-slate-500 text-sm leading-relaxed mb-6">
                      {dataset.description}
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-8">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 group-hover:bg-slate-100 transition-colors">
                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-1 tracking-wider">Participants</div>
                        <div className="text-2xl font-black text-slate-800">{dataset.rows.toLocaleString()}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 group-hover:bg-slate-100 transition-colors">
                        <div className="text-[10px] font-bold text-slate-400 uppercase mb-1 tracking-wider">Features</div>
                        <div className="text-2xl font-black text-slate-800">{dataset.cols}</div>
                      </div>
                    </div>

                    <div className="mt-auto">
                      {isAuthorized ? (
                        <button
                          className="w-full py-3 bg-emerald-600 cursor-default text-white rounded-xl font-bold shadow-md transition-all flex items-center justify-center gap-2"
                        >
                          <Download className="w-5 h-5" /> Download Dataset
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRequestAccess(dataset)}
                          className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold shadow-md transition-all flex items-center justify-center gap-2"
                        >
                          <Lock className="w-5 h-5" /> Request Access
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Request Modal */}
        {syntheticAccessModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 scale-in-center transition-transform">
              <div className="bg-slate-50 px-8 py-6 border-b border-slate-200 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Access Request</h3>
                    <p className="text-xs text-slate-500 font-medium">Dataset: {requestingDataset?.name}</p>
                  </div>
                </div>
                <button onClick={() => setSyntheticAccessModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
              </div>

              <form onSubmit={handleSubmitRequest} className="p-8 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Full Name</label>
                    <input
                      required
                      type="text"
                      value={syntheticForm.fullName}
                      onChange={e => setSyntheticForm({ ...syntheticForm, fullName: e.target.value })}
                      placeholder="Full Name"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all font-medium"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 text-left">
                    <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Email Address</label>
                    <input
                      required
                      type="email"
                      value={syntheticForm.email}
                      onChange={e => setSyntheticForm({ ...syntheticForm, email: e.target.value })}
                      placeholder="Email Address"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all font-medium"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="flex flex-col gap-1.5 text-left">
                    <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Institution</label>
                    <input
                      required
                      type="text"
                      value={syntheticForm.institution}
                      onChange={e => setSyntheticForm({ ...syntheticForm, institution: e.target.value })}
                      placeholder="Organisation Name"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all font-medium"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 text-left">
                    <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Job Role / Position</label>
                    <input
                      required
                      type="text"
                      value={syntheticForm.jobRole}
                      onChange={e => setSyntheticForm({ ...syntheticForm, jobRole: e.target.value })}
                      placeholder="Data Scientist, student, etc."
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all font-medium"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 text-left">
                  <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Planned Use for Data</label>
                  <textarea
                    required
                    rows="3"
                    value={syntheticForm.plannedUse}
                    onChange={e => setSyntheticForm({ ...syntheticForm, plannedUse: e.target.value })}
                    placeholder="Describe how you intend to use this synthetic data..."
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all font-medium resize-none text-left"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-bold shadow-lg transition-all transform active:scale-95 mt-4"
                >
                  Submit
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return (<div className="min-h-screen bg-slate-50 flex items-center justify-center flex-col gap-4"><Loader2 className="w-10 h-10 text-purple-600 animate-spin" /><h2 className="text-xl font-semibold text-slate-700">Loading Cohort Discovery Tools...</h2></div>);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 font-sans">
      <header className="h-16 md:h-20 border-b border-slate-200/80 px-4 md:px-6 flex items-center justify-between bg-white/80 backdrop-blur-xl z-50 sticky top-0 shadow-sm">
        {/* Left Side: Mobile Menu & Logo */}
        <div className="flex items-center gap-3 md:gap-6">
          <button
            className="lg:hidden p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <div 
            className="flex items-center cursor-pointer group" 
            onClick={() => { setCurrentView('home'); setIsMobileMenuOpen(false); }}
          >
            <img
              src={process.env.PUBLIC_URL + '/NDP_Logo_New2.png'}
              alt="Logo"
              className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </div>
        </div>

        {/* Center: Desktop Navigation */}
        <nav className="hidden lg:flex items-center bg-slate-100/80 p-1.5 rounded-2xl border border-slate-200/50">
          <button 
            onClick={() => setCurrentView('home')} 
            className={classNames(
              "px-4 py-2 text-sm font-semibold rounded-xl transition-all flex items-center whitespace-nowrap", 
              currentView === 'home' 
                ? "bg-white text-purple-700 shadow-sm ring-1 ring-black/5" 
                : "text-slate-600 hover:bg-slate-200/50 hover:text-slate-900"
            )}
          >
            Home
          </button>
          
          <div className="relative group">
            <button 
              onClick={() => setCurrentView('data-discovery')} 
              className={classNames(
                "px-4 py-2 text-sm font-semibold rounded-xl transition-all flex items-center gap-1 whitespace-nowrap", 
                ['data-discovery', 'cohort-library', 'matrix', 'directory', 'overlap', 'network'].includes(currentView) 
                  ? "bg-white text-purple-700 shadow-sm ring-1 ring-black/5" 
                  : "text-slate-600 hover:bg-slate-200/50 hover:text-slate-900"
              )}
            >
              Data Discovery
              <ChevronDown className="w-4 h-4 ml-0.5 opacity-60 group-hover:rotate-180 transition-transform" />
            </button>
            <div className="absolute left-0 top-full mt-2 w-48 bg-white border border-slate-200 shadow-xl rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden">
              <div className="py-2">
                {[
                  { id: 'cohort-library', label: 'Cohort Library' },
                  { id: 'matrix', label: 'Matrix View' },
                  { id: 'directory', label: 'Variable Directory' },
                  { id: 'overlap', label: 'Compare Cohorts' },
                  { id: 'network', label: 'Network Graph' },
                ].map(subItem => (
                  <button
                    key={subItem.id}
                    onClick={() => setCurrentView(subItem.id)}
                    className={classNames(
                      "w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-purple-50 hover:text-purple-700",
                      currentView === subItem.id ? "bg-purple-50 text-purple-700 font-bold" : "text-slate-600"
                    )}
                  >
                    {subItem.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button 
            onClick={() => setCurrentView('data-access')} 
            className={classNames(
              "px-4 py-2 text-sm font-semibold rounded-xl transition-all flex items-center whitespace-nowrap", 
              currentView === 'data-access' 
                ? "bg-white text-purple-700 shadow-sm ring-1 ring-black/5" 
                : "text-slate-600 hover:bg-slate-200/50 hover:text-slate-900"
            )}
          >
            Data Access
          </button>
          
          <div className="w-px h-6 bg-slate-300 mx-2" />
          <button 
            onClick={() => setCurrentView('global-search')} 
            className={classNames(
              "px-4 py-2 text-sm font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap group", 
              currentView === 'global-search' 
                ? "bg-white shadow-sm ring-1 ring-black/5" 
                : "hover:bg-purple-50"
            )}
          >
            <Sparkles className={classNames("w-4 h-4", currentView === 'global-search' ? "text-brand-pink" : "text-purple-500 group-hover:text-brand-pink")} />
            <span className="bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue bg-clip-text text-transparent">AI Search</span>
          </button>
        </nav>

        {/* Right Side: Cart Action */}
        <div className="flex items-center">
          <button 
            onClick={() => { setCurrentView('cart'); setIsMobileMenuOpen(false); }} 
            className={classNames(
              "flex items-center gap-2.5 px-4 py-2 md:py-2.5 rounded-xl transition-all duration-300 border font-semibold", 
              (cart.length + imagingCart.length + genomicsCart.length) > 0 
                ? "bg-purple-600 hover:bg-purple-700 text-white border-purple-600 shadow-lg shadow-purple-600/25" 
                : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200 shadow-sm"
            )}
          >
            <ShoppingCart className={classNames("w-5 h-5", (cart.length + imagingCart.length + genomicsCart.length) > 0 ? "animate-bounce" : "")} />
            <span className="text-sm hidden sm:inline-block">Cart</span>
            {(cart.length + imagingCart.length + genomicsCart.length) > 0 && (
              <span className="bg-white text-purple-700 min-w-[24px] h-[24px] flex items-center justify-center rounded-full text-xs font-bold leading-none shadow-sm">
                {cart.length + imagingCart.length + genomicsCart.length}
              </span>
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white/95 backdrop-blur-3xl border-b border-slate-200 shadow-2xl z-50 lg:hidden flex flex-col p-4 gap-2 animate-in slide-in-from-top-2 duration-200">
            <button
                onClick={() => { setCurrentView('home'); setIsMobileMenuOpen(false); }}
                className={classNames(
                  "flex items-center p-3.5 rounded-2xl text-left transition-all border",
                  currentView === 'home'
                    ? "bg-purple-50 text-purple-700 font-bold border-purple-100 shadow-sm"
                    : "bg-white text-slate-700 hover:bg-slate-50 font-medium border-transparent active:border-slate-100"
                )}
            >
              <div className={classNames(
                  "w-10 h-10 rounded-xl flex items-center justify-center mr-4 shadow-sm",
                  currentView === 'home' ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-500"
                )}>
                  <LayoutGrid className="w-5 h-5" />
                </div>
                <div className="flex-1 text-[15px]">Home Dashboard</div>
            </button>

            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-2 space-y-1">
              <button
                onClick={() => { setCurrentView('data-discovery'); setIsMobileMenuOpen(false); }}
                className={classNames(
                  "flex items-center p-3.5 rounded-2xl text-left transition-all border w-full",
                  currentView === 'data-discovery'
                    ? "bg-purple-50 text-purple-700 font-bold border-purple-100 shadow-sm"
                    : "bg-transparent text-slate-700 hover:bg-slate-100 font-bold border-transparent"
                )}
              >
                  <div className="flex-1 text-[15px]">Data Discovery</div>
              </button>
            {[
              { id: 'cohort-library', label: 'Cohort Library', icon: <Package className="w-4 h-4" /> },
              { id: 'matrix', label: 'Matrix View', icon: <Table className="w-4 h-4" /> },
              { id: 'directory', label: 'Variable Directory', icon: <Database className="w-4 h-4" /> },
              { id: 'overlap', label: 'Compare Cohorts', icon: <ArrowRightLeft className="w-4 h-4" /> },
              { id: 'network', label: 'Network Graph', icon: <Share2 className="w-4 h-4" /> },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  setIsMobileMenuOpen(false);
                }}
                className={classNames(
                  "flex w-full items-center p-2.5 rounded-xl text-left transition-all pl-6",
                  currentView === item.id
                    ? "bg-purple-50 text-purple-700 font-bold shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 font-medium"
                )}
              >
                <div className={classNames(
                  "flex items-center justify-center mr-3",
                  currentView === item.id ? "text-purple-600" : "text-slate-400"
                )}>
                  {item.icon}
                </div>
                <div className="flex-1 text-sm">
                  <span>{item.label}</span>
                </div>
              </button>
            ))}
            </div>

            {[
              { id: 'data-access', label: 'Data Access Process', icon: <FileText className="w-5 h-5" /> },
              { id: 'global-search', label: 'AI Semantic Search', icon: <Sparkles className="w-5 h-5" /> },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  setIsMobileMenuOpen(false);
                }}
                className={classNames(
                  "flex items-center p-3.5 rounded-2xl text-left transition-all border",
                  currentView === item.id
                    ? "bg-purple-50 text-purple-700 font-bold border-purple-100 shadow-sm"
                    : "bg-white text-slate-700 hover:bg-slate-50 font-medium border-transparent active:border-slate-100"
                )}
              >
                <div className={classNames(
                  "w-10 h-10 rounded-xl flex items-center justify-center mr-4 shadow-sm",
                  currentView === item.id ? (item.id === 'global-search' ? 'bg-indigo-600' : "bg-purple-600") : "bg-slate-100 text-slate-500"
                )}>
                  {item.icon}
                </div>
                <div className="flex-1 text-[15px]">
                  {item.id === 'global-search' ? (
                       <span className="bg-gradient-to-r from-brand-pink via-brand-purple to-brand-blue bg-clip-text text-transparent font-extrabold">{item.label}</span>
                   ) : (
                       <span>{item.label}</span>
                   )}
                </div>
                {currentView === item.id && <ChevronRight className="w-5 h-5 text-purple-600" />}
              </button>
            ))}
          </div>
        )}
      </header>
      <main className="flex-1 flex flex-col">
        {currentView === 'home' && renderHomeView()}
        {currentView === 'data-discovery' && renderDataDiscoveryView()}
        {currentView === 'cohort-library' && renderCohortLibraryView()}
        {currentView === 'matrix' && renderMatrixView()}
        {currentView === 'directory' && renderDirectoryView()}
        {currentView === 'global-search' && renderGlobalSearchView()}
        {currentView === 'overlap' && renderOverlapView()}
        {currentView === 'network' && renderNetworkView()}
        {currentView === 'data-access' && renderDataAccessView()}
        {currentView === 'synthetic' && renderSyntheticLibraryView()}
        {currentView === 'cohort' && renderCohortView()}
        {currentView === 'cart' && renderCartView()}
      </main>

      <RecommendationPopup
        visible={recPopup.visible}
        onClose={() => setRecPopup(prev => ({ ...prev, visible: false }))}
        sourceVar={recPopup.sourceVar}
        recommendations={recPopup.recommendations}
        loading={isRecPopupLoading}
        onAdd={(id) => setCart(prev => prev.includes(id) ? prev : [...prev, id])}
        cart={cart}
      />

      {/* Similar Variables Modal */}
      {similarModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex bg-slate-50 border-b border-slate-200 items-start justify-between p-6">
              <div>
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /> Similar Variables found via AI</h2>
                {similarOriginVar && <p className="text-sm text-slate-500 mt-1">Based on semantic match with: <strong>{similarOriginVar['Variable Name']}</strong> - {similarOriginVar['Variable Label']}</p>}
              </div>
              <div className="flex items-center gap-2">
                {!isSimilarLoading && similarVars.length > 0 && (
                  <button
                    onClick={() => {
                      const newIds = similarVars.map(v => v.id).filter(id => !cart.includes(id));
                      if (newIds.length > 0) {
                        setCart(prev => [...prev, ...newIds]);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-bold transition-colors shadow-sm"
                  >
                    <ShoppingCart className="w-4 h-4" /> Add All ({similarVars.filter(v => !cart.includes(v.id)).length})
                  </button>
                )}
                <button onClick={() => setSimilarModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 custom-scrollbar">
              {isSimilarLoading ? (
                <div className="flex flex-col items-center justify-center py-20"><Loader2 className="w-8 h-8 text-purple-500 animate-spin mb-4" /><p className="text-slate-500 font-medium">Analysing semantic vector embeddings database...</p></div>
              ) : similarVars.length === 0 ? (
                <div className="text-center py-20 text-slate-500">No highly similar variables found.</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {similarVars.map(variable => {
                    const isSelected = cart.includes(variable.id);
                    return (
                      <div key={variable.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-4 hover:border-purple-300 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-xs font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded uppercase">{variable['Variable Name']}</span>
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{variable.Cohort} &bull; {variable.Category}</span>
                            <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full ml-auto border border-emerald-100">{Math.round(variable.similarityScore * 100)}% Match</span>
                          </div>
                          <p className="text-sm text-slate-600 font-medium truncate" title={variable['Variable Label']}>{variable['Variable Label']}</p>
                        </div>
                        <button onClick={() => toggleCartItem(variable.id)} className={classNames("px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm flex items-center gap-2", isSelected ? "bg-purple-100 text-purple-700 hover:bg-purple-200" : "bg-purple-600 text-white hover:bg-purple-700")}>
                          {isSelected ? <><Check className="w-4 h-4" /> Added</> : <><ShoppingCart className="w-4 h-4" /> Add</>}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}